import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import {
  IconBolt,
  IconCalendar,
  IconCalendarDays,
  IconTrophy,
  IconLayers,
  IconVideo,
  IconImage,
  IconMessage,
  IconPlus,
  IconEye,
  IconClock,
  IconCheckCircle,
  IconUsers,
  IconEdit,
  IconSearch,
  IconPhone,
  IconAlertCircle,
  IconSparkles,
  IconCheck,
  IconX,
  IconDownload
} from '../components/Icons';
import api from '../services/api';
import toast from 'react-hot-toast';

const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const BRAND_STYLES: Record<string, { color: string; bg: string; glow: string }> = {
  'espacio vital': {
    color: '#0060df',
    bg: 'rgba(219, 234, 254, 0.95)',
    glow: 'rgba(0, 112, 243, 0.18)'
  },
  'edwcar': {
    color: '#047857',
    bg: 'rgba(209, 250, 229, 0.95)',
    glow: 'rgba(16, 185, 129, 0.18)'
  },
  'futura': {
    color: '#c2410c',
    bg: 'rgba(255, 237, 213, 0.95)',
    glow: 'rgba(249, 115, 22, 0.18)'
  },
  'pentagono': {
    color: '#7c3aed',
    bg: 'rgba(243, 232, 255, 0.95)',
    glow: 'rgba(124, 58, 237, 0.18)'
  },
  'baruk': {
    color: '#db2777',
    bg: 'rgba(253, 231, 243, 0.95)',
    glow: 'rgba(219, 39, 119, 0.18)'
  },
  'genius': {
    color: '#b45309',
    bg: 'rgba(254, 243, 199, 0.95)',
    glow: 'rgba(245, 158, 11, 0.18)'
  },
  'reditos': {
    color: '#dc2626',
    bg: 'rgba(254, 226, 226, 0.95)',
    glow: 'rgba(239, 68, 68, 0.18)'
  },
  'uan': {
    color: '#0f766e',
    bg: 'rgba(204, 251, 241, 0.95)',
    glow: 'rgba(15, 118, 110, 0.18)'
  },
  'nexos': {
    color: '#65a30d',
    bg: 'rgba(236, 252, 203, 0.95)',
    glow: 'rgba(132, 204, 22, 0.18)'
  },
  'zoik': {
    color: '#4338ca',
    bg: 'rgba(224, 231, 255, 0.95)',
    glow: 'rgba(67, 56, 202, 0.18)'
  }
};

const DYNAMIC_PALETTES = [
  { color: '#0060df', bg: 'rgba(219, 234, 254, 0.9)', glow: 'rgba(0, 112, 243, 0.14)' },
  { color: '#047857', bg: 'rgba(209, 250, 229, 0.9)', glow: 'rgba(16, 185, 129, 0.14)' },
  { color: '#7c3aed', bg: 'rgba(243, 232, 255, 0.9)', glow: 'rgba(124, 58, 237, 0.14)' },
  { color: '#b45309', bg: 'rgba(254, 243, 199, 0.9)', glow: 'rgba(245, 158, 11, 0.14)' },
  { color: '#e11d48', bg: 'rgba(255, 228, 230, 0.9)', glow: 'rgba(225, 29, 72, 0.14)' },
  { color: '#0284c7', bg: 'rgba(224, 242, 254, 0.9)', glow: 'rgba(2, 132, 199, 0.14)' },
  { color: '#0f766e', bg: 'rgba(204, 251, 241, 0.9)', glow: 'rgba(15, 118, 110, 0.14)' },
  { color: '#c2410c', bg: 'rgba(255, 237, 213, 0.9)', glow: 'rgba(194, 65, 12, 0.14)' },
];

const getBrandStyle = (brandName?: string) => {
  if (!brandName) return DYNAMIC_PALETTES[0];
  const lower = brandName.toLowerCase();
  for (const [key, val] of Object.entries(BRAND_STYLES)) {
    if (lower.includes(key)) return val;
  }
  let hash = 0;
  for (let i = 0; i < brandName.length; i++) hash = brandName.charCodeAt(i) + ((hash << 5) - hash);
  const idx = Math.abs(hash) % DYNAMIC_PALETTES.length;
  return DYNAMIC_PALETTES[idx];
};

if (typeof document !== 'undefined' && !document.getElementById('team-liquid-glass-css')) {
  const style = document.createElement('style');
  style.id = 'team-liquid-glass-css';
  style.innerHTML = `
    .liquid-glass-card {
      background: rgba(255, 255, 255, 0.65);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      border: 1.5px solid rgba(255, 255, 255, 0.85);
      border-radius: 20px;
      box-shadow: 0 8px 32px 0 rgba(15, 42, 74, 0.06), inset 0 1px 2px 0 rgba(255, 255, 255, 0.9);
      transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    }
    .liquid-glass-card:hover {
      transform: translateY(-3px);
      box-shadow: 0 12px 36px 0 rgba(15, 42, 74, 0.1), inset 0 1px 2px 0 rgba(255, 255, 255, 0.95);
    }
    .liquid-glass-panel {
      background: rgba(255, 255, 255, 0.72);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1.5px solid rgba(255, 255, 255, 0.85);
      border-radius: 20px;
      box-shadow: 0 10px 30px rgba(15, 42, 74, 0.06), inset 0 1px 1px rgba(255, 255, 255, 0.8);
    }
    .liquid-glass-item {
      background: rgba(255, 255, 255, 0.6);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.75);
      border-radius: 14px;
      transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    }
    .liquid-glass-item:hover {
      background: rgba(255, 255, 255, 0.9);
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(15, 42, 74, 0.08);
      border-color: rgba(0, 112, 243, 0.3);
    }
    .liquid-glass-modal {
      background: rgba(255, 255, 255, 0.88);
      backdrop-filter: blur(24px) saturate(180%);
      -webkit-backdrop-filter: blur(24px) saturate(180%);
      border: 1.5px solid rgba(255, 255, 255, 0.95);
      box-shadow: 0 24px 60px rgba(15, 42, 74, 0.18), inset 0 1px 2px rgba(255, 255, 255, 0.9);
    }
    @keyframes pulseGreenDot {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.2); opacity: 0.75; }
    }
    .pulse-online {
      animation: pulseGreenDot 2s infinite ease-in-out;
    }
  `;
  document.head.appendChild(style);
}

// Roulette Counter Component (Dashboard Standard)
const RouletteNumber: React.FC<{ value: number | string; duration?: number }> = ({ value, duration = 1200 }) => {
  const target = typeof value === 'number' ? value : parseInt(String(value), 10) || 0;
  const [display, setDisplay] = useState<number>(0);

  useEffect(() => {
    if (target === 0) {
      const frames = [6, 3, 1, 0];
      let idx = 0;
      const interval = setInterval(() => {
        if (idx < frames.length) {
          setDisplay(frames[idx]);
          idx++;
        } else {
          clearInterval(interval);
          setDisplay(0);
        }
      }, 70);
      return () => clearInterval(interval);
    }

    let start: number | null = null;
    let animId: number;

    const animate = (timestamp: number) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / duration, 1);
      const ease = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      const currentVal = Math.round(ease * target);
      setDisplay(currentVal);

      if (progress < 1) {
        animId = requestAnimationFrame(animate);
      } else {
        setDisplay(target);
      }
    };

    animId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animId);
  }, [target, duration]);

  return <span>{display}</span>;
};

interface UserItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  avatar?: string;
  position?: string;
  baseSalary?: number;
  contractType?: string;
  birthDate?: string;
  address?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
}

interface BrandItem {
  id: string;
  name: string;
  logoUrl?: string;
}


export interface CollaboratorMetrics {
  totalPieces: number;
  monthPieces: number;
  totalApproved: number;
  approvalRate: number;
  activeBrandsCount: number;
}

export interface CollaboratorItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  active: boolean;
  avatar?: string;
  coverPhoto?: string;
  position?: string;
  bio?: string;
  birthDate?: string;
  address?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  contractType?: string;
  contractStart?: string;
  contractEnd?: string;
  baseSalary?: number;
  contractReminderSent?: boolean;
  contractStatus: 'INDEFINIDO' | 'VIGENTE' | 'POR_VENCER' | 'VENCIDO' | 'SIN_FECHA';
  daysRemaining: number | null;
  isBirthdayToday: boolean;
  daysUntilBirthday: number | null;
  metrics: CollaboratorMetrics;
}


export interface OvertimeRecordItem {
  id: string;
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    position?: string;
    role?: string;
    avatar?: string;
    contractType?: string;
    baseSalary?: number;
  };
  date: string;
  startTime: string;
  endTime: string;
  hours: number;
  reason: string;
  status: 'PENDIENTE' | 'APROBADO' | 'RECHAZADO';
  approvedById?: string;
  approvedAt?: string;
  approvalNotes?: string;
  diurnaHours: number;
  nocturnaHours: number;
  festivaDiurnaHours: number;
  festivaNocturnaHours: number;
  hourlyRate: number;
  totalAmount: number;
  createdAt: string;
}


export interface VacationRecordItem {
  id: string;
  userId: string;
  recipientId?: string;
  recipientName?: string;
  startDate: string;
  endDate: string;
  daysRequested: number;
  reason: string;
  details?: string;
  salaryBase: number;
  calculatedValue: number;
  status: 'PENDIENTE' | 'APROBADO' | 'RECHAZADO';
  responseNotes?: string;
  reviewedAt?: string;
  approvedById?: string;
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    position?: string;
    role?: string;
    avatar?: string;
    contractType?: string;
    baseSalary?: number;
  };
}

interface PieceItem {
  id: string;
  title: string;
  brandId: string;
  brand?: BrandItem;
  status: string;
  type?: string;
  files?: any[];
  publishDate?: string;
  assignedToId?: string;
  assignedTo?: UserItem;
  createdById?: string;
  createdBy?: UserItem;
  processCard?: any;
  comments?: any[];
}

export const getPieceUnits = (p: any): number => {
  if (!p) return 0;
  if (p.type === 'CARRUSEL' || (Array.isArray(p.files) && p.files.length > 1)) {
    return Array.isArray(p.files) && p.files.length > 0 ? p.files.length : 1;
  }
  if (p.type === 'CUADRO_MATERIAL' && Array.isArray(p.driveFiles) && p.driveFiles.length > 0) {
    return p.driveFiles.length;
  }
  return 1;
};

export const countUnits = (list: any[]) => {
  if (!Array.isArray(list)) return 0;
  return list.reduce((acc, p) => acc + getPieceUnits(p), 0);
};

export const Team: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<UserItem[]>([]);
  const [brands, setBrands] = useState<BrandItem[]>([]);
  const [pieces, setPieces] = useState<PieceItem[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('MY_WORKSPACE');
  const [loading, setLoading] = useState(true);

  // Vistas: 'FLOW' | 'CALENDAR' | 'PERFORMANCE'
  const [mainView, setMainView] = useState<'FLOW' | 'CALENDAR' | 'PERFORMANCE' | 'COLLABORATORS' | 'OVERTIME' | 'VACATIONS'>('FLOW');

  // Control de Mes y Año para el Calendario
  const now = new Date();
  const [month, setMonth] = useState<number>(now.getMonth());
  const [year, setYear] = useState<number>(now.getFullYear());

  // Modal Asignar Tarea
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [newTaskForm, setNewTaskForm] = useState({
    title: '',
    brandId: '',
    type: 'ESTATICA',
    publishDate: new Date().toISOString().split('T')[0],
    assignedToId: ''
  });
  const [savingTask, setSavingTask] = useState(false);

  const isAdminOrCuentas = ['ADMIN', 'CUENTAS'].includes(user?.role || '');

  // Estados para Módulo de Colaboradores (RRHH & Métricas Reales)
  const [collaborators, setCollaborators] = useState<CollaboratorItem[]>([]);
  const [loadingCollaborators, setLoadingCollaborators] = useState(false);
  const [collabSearch, setCollabSearch] = useState('');
  const [collabRoleFilter, setCollabRoleFilter] = useState('TODOS');
  const [collabContractFilter, setCollabContractFilter] = useState('TODOS');
  const [editCollabModalOpen, setEditCollabModalOpen] = useState(false);
  const [selectedCollab, setSelectedCollab] = useState<CollaboratorItem | null>(null);
  const [savingCollab, setSavingCollab] = useState(false);
  const [checkingReminders, setCheckingReminders] = useState(false);
  const [sendingReminderId, setSendingReminderId] = useState<string | null>(null);

  const [collabForm, setCollabForm] = useState({
    firstName: '',
    lastName: '',
    position: '',
    birthDate: '',
    address: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    contractType: '',
    contractStart: '',
    contractEnd: '',
    baseSalary: ''
  });

  const loadCollaborators = async () => {
    if (!isAdminOrCuentas) return;
    setLoadingCollaborators(true);
    try {
      const res = await api.get('/users/collaborators');
      if (Array.isArray(res.data)) {
        setCollaborators(res.data);
      }
    } catch (e: any) {
      console.error('Error cargando colaboradores:', e);
    } finally {
      setLoadingCollaborators(false);
    }
  };

  const handleOpenEditCollab = (c: CollaboratorItem) => {
    setSelectedCollab(c);
    setCollabForm({
      firstName: c.firstName || '',
      lastName: c.lastName || '',
      position: c.position || '',
      birthDate: c.birthDate ? new Date(c.birthDate).toISOString().split('T')[0] : '',
      address: c.address || '',
      emergencyContactName: c.emergencyContactName || '',
      emergencyContactPhone: c.emergencyContactPhone || '',
      contractType: c.contractType || '',
      contractStart: c.contractStart ? new Date(c.contractStart).toISOString().split('T')[0] : '',
      contractEnd: c.contractEnd ? new Date(c.contractEnd).toISOString().split('T')[0] : '',
      baseSalary: c.baseSalary ? String(c.baseSalary) : ''
    });
    setEditCollabModalOpen(true);
  };

  const handleSaveCollab = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCollab) return;
    setSavingCollab(true);
    try {
      await api.put(`/users/${selectedCollab.id}/collaborator`, collabForm);
      toast.success('Información del colaborador actualizada correctamente');
      setEditCollabModalOpen(false);
      await loadCollaborators();
      loadOvertimeData();
      await loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al actualizar colaborador');
    } finally {
      setSavingCollab(false);
    }
  };

  const handleSendSingleReminder = async (c: CollaboratorItem) => {
    setSendingReminderId(c.id);
    try {
      const res = await api.post(`/users/hr/send-contract-reminder/${c.id}`);
      if (res.data?.success) {
        toast.success(`Recordatorio formal enviado a Alejandra Fernández (${c.daysRemaining} días restantes)`);
        await loadCollaborators();
      loadOvertimeData();
      } else {
        toast.error('No se pudo enviar el correo de recordatorio');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Error enviando recordatorio');
    } finally {
      setSendingReminderId(null);
    }
  };

  
  // Estados para Módulo de Horas Extras y Liquidaciones (Módulo 2)
  const [overtimeRecords, setOvertimeRecords] = useState<OvertimeRecordItem[]>([]);
  const [loadingOvertime, setLoadingOvertime] = useState(false);
  const [overtimeStats, setOvertimeStats] = useState({
    pendingCount: 0,
    approvedThisMonthCount: 0,
    totalHoursThisMonth: 0,
    totalAmountThisMonth: 0,
    collaboratorsCount: 0
  });
  const [showRegisterOvertimeModal, setShowRegisterOvertimeModal] = useState(false);
  const [registerOvertimeForm, setRegisterOvertimeForm] = useState({
    date: new Date().toISOString().split('T')[0],
    startTime: '18:00',
    endTime: '21:00',
    reason: ''
  });
  const [savingOvertime, setSavingOvertime] = useState(false);
  const [reviewingOvertimeId, setReviewingOvertimeId] = useState<string | null>(null);
  const [overtimeStatusFilter, setOvertimeStatusFilter] = useState('TODOS');
  const [overtimeSearch, setOvertimeSearch] = useState('');

  const isAlejandra = (user?.email || '').toLowerCase() === 'contabilidadzoikagency@gmail.com';
  useEffect(() => {
    if (isAlejandra) {
      setMainView('PERFORMANCE');
    }
  }, [isAlejandra]);
  const isNicolas = (user?.email || '').toLowerCase() === 'fernandeznicolas1005@gmail.com';
  const canManageOvertime = isAdminOrCuentas || isAlejandra || isNicolas;

  const loadOvertimeData = async () => {
    setLoadingOvertime(true);
    try {
      const [recRes, statsRes] = await Promise.all([
        api.get('/overtime').catch(() => ({ data: [] })),
        api.get('/overtime/stats/summary').catch(() => ({ data: null }))
      ]);
      setOvertimeRecords(Array.isArray(recRes.data) ? recRes.data : []);
      if (statsRes.data) {
        setOvertimeStats(statsRes.data);
      }
    } catch (e: any) {
      console.error('Error cargando horas extras:', e);
    } finally {
      setLoadingOvertime(false);
    }
  };

  const handleRegisterOvertimeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingOvertime(true);
    try {
      const res = await api.post('/overtime', registerOvertimeForm);
      if (res.data?.success) {
        toast.success('Horas extras registradas y enviadas a Nicolás Fernández para aprobación');
        setShowRegisterOvertimeModal(false);
        setRegisterOvertimeForm({
          date: new Date().toISOString().split('T')[0],
          startTime: '18:00',
          endTime: '21:00',
          reason: ''
        });
        await loadOvertimeData();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al registrar horas extras');
    } finally {
      setSavingOvertime(false);
    }
  };

  const handleReviewOvertimeDecision = async (id: string, action: 'APROBADO' | 'RECHAZADO', notes?: string) => {
    setReviewingOvertimeId(id);
    try {
      const res = await api.put(`/overtime/${id}/review`, { action, approvalNotes: notes });
      if (res.data?.success) {
        if (action === 'APROBADO') {
          toast.success('Horas extras aprobadas. Cálculo liquidado y PDF generado para Alejandra Fernández.');
        } else {
          toast.success('Solicitud de horas extras rechazada.');
        }
        await loadOvertimeData();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al revisar horas extras');
    } finally {
      setReviewingOvertimeId(null);
    }
  };

  const handleDownloadOvertimePDF = async (recordId: string, collabName: string) => {
    const toastId = toast.loading('Generando comprobante oficial membretado con logo ZOIK...');
    try {
      const res = await api.get(`/overtime/${recordId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('PDF oficial generado con éxito', { id: toastId });
    } catch {
      toast.error('Error al generar el documento PDF', { id: toastId });
    }
  };

  const handleCheckAllReminders = async () => {
    setCheckingReminders(true);
    try {
      const res = await api.post('/users/hr/check-all-contract-reminders');
      if (res.data?.sentCount > 0) {
        toast.success(`Se enviaron ${res.data.sentCount} recordatorio(s) contractuales a Alejandra Fernández`);
      } else {
        toast.success('No hay contratos pendientes por notificar (<= 30 días)');
      }
      await loadCollaborators();
      loadOvertimeData();
    } catch (e: any) {
      toast.error('Error al comprobar recordatorios');
    } finally {
      setCheckingReminders(false);
    }
  };



  // Estados para Módulo de Solicitudes y Vacaciones (Módulo 5)
  const [vacationRecords, setVacationRecords] = useState<VacationRecordItem[]>([]);
  const [loadingVacations, setLoadingVacations] = useState(false);
  const [vacationStats, setVacationStats] = useState({
    pendingCount: 0,
    approvedThisYearCount: 0,
    totalDaysThisYear: 0,
    totalAmountThisYear: 0,
    collaboratorsCount: 0
  });
  const [showRequestVacationModal, setShowRequestVacationModal] = useState(false);
  const [savingVacation, setSavingVacation] = useState(false);
  const [reviewingVacationId, setReviewingVacationId] = useState<string | null>(null);
  const [vacationStatusFilter, setVacationStatusFilter] = useState('TODAS');
  const [vacationSearch, setVacationSearch] = useState('');

  // Formulario de Solicitud de Vacaciones
  const [requestVacationForm, setRequestVacationForm] = useState({
    recipientName: 'Alejandra Fernández — Directora de Contabilidad & RRHH',
    reason: 'Vacaciones Legales Anuales (CST Art. 186)',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 15 * 86400000).toISOString().split('T')[0],
    daysRequested: 15,
    details: ''
  });

  const loadVacationData = async () => {
    setLoadingVacations(true);
    try {
      const [recRes, statsRes] = await Promise.all([
        api.get('/vacations').catch(() => ({ data: [] })),
        api.get('/vacations/stats/summary').catch(() => ({ data: null }))
      ]);
      setVacationRecords(Array.isArray(recRes.data) ? recRes.data : []);
      if (statsRes.data) {
        setVacationStats(statsRes.data);
      }
    } catch (e: any) {
      console.error('Error cargando solicitudes de vacaciones:', e);
    } finally {
      setLoadingVacations(false);
    }
  };

  const handleRequestVacationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingVacation(true);
    try {
      const res = await api.post('/vacations', requestVacationForm);
      if (res.data?.id) {
        toast.success('¡Solicitud de vacaciones radicada exitosamente! Enviada a Alejandra Fernández para revisión contable');
        setShowRequestVacationModal(false);
        setRequestVacationForm({
          recipientName: 'Alejandra Fernández — Directora de Contabilidad & RRHH',
          reason: 'Vacaciones Legales Anuales (CST Art. 186)',
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date(Date.now() + 15 * 86400000).toISOString().split('T')[0],
          daysRequested: 15,
          details: ''
        });
        await loadVacationData();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al radicar solicitud de vacaciones');
    } finally {
      setSavingVacation(false);
    }
  };

  const handleReviewVacation = async (id: string, status: 'APROBADO' | 'RECHAZADO', notes: string = '') => {
    setReviewingVacationId(id);
    const toastId = toast.loading(`${status === 'APROBADO' ? 'Aprobando' : 'Rechazando'} solicitud y emitiendo resolución...`);
    try {
      await api.put(`/vacations/${id}/review`, { status, responseNotes: notes });
      toast.success(status === 'APROBADO' ? '¡Solicitud Aprobada! Comprobante oficial emitido en PDF' : 'Solicitud rechazada debidamente', { id: toastId });
      await loadVacationData();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al procesar solicitud', { id: toastId });
    } finally {
      setReviewingVacationId(null);
    }
  };

  const handleDownloadVacationPDF = async (recordId: string, collabName: string) => {
    const toastId = toast.loading('Generando comprobante oficial de vacaciones con logo ZOIK y folio...');
    try {
      const res = await api.get(`/vacations/${recordId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('PDF oficial generado con éxito', { id: toastId });
    } catch {
      toast.error('Error al generar el documento PDF de vacaciones', { id: toastId });
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [uRes, bRes, pRes] = await Promise.all([
        api.get('/users').catch(() => ({ data: [] })),
        api.get('/brands').catch(() => ({ data: [] })),
        api.get('/pieces').catch(() => ({ data: [] }))
      ]);
      setUsers(Array.isArray(uRes.data) ? uRes.data : []);
      setBrands(Array.isArray(bRes.data) ? bRes.data : []);
      setPieces(Array.isArray(pRes.data) ? pRes.data : []);
      loadCollaborators();
      loadOvertimeData();
      loadVacationData();
    } catch {
      toast.error('Error al cargar datos del workspace');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const isViewingSpecificUser = selectedUserId !== 'MY_WORKSPACE';
  const inspectedUser = useMemo(() => {
    if (!isViewingSpecificUser) return user || ({} as any);
    return users.find(u => u.id === selectedUserId) || user || ({} as any);
  }, [users, selectedUserId, user, isViewingSpecificUser]);

  // Filtrado Estricto de Tareas
  const filteredTasks = useMemo(() => {
    if (!Array.isArray(pieces)) return [];
    if (isViewingSpecificUser || !isAdminOrCuentas) {
      const targetId = isViewingSpecificUser ? selectedUserId : (user?.id || (user as any)?.userId);
      return pieces.filter(p =>
        p.assignedToId === targetId ||
        p.assignedTo?.id === targetId ||
        p.createdById === targetId ||
        p.createdBy?.id === targetId ||
        p.processCard?.assignedUserId === targetId
      );
    }

    return pieces;
  }, [pieces, selectedUserId, isViewingSpecificUser, isAdminOrCuentas, user]);

  // Clasificación de Tareas
  const classifiedTasks = useMemo(() => {
    const pendingApproval: PieceItem[] = [];
    const inProduction: PieceItem[] = [];
    const withChanges: PieceItem[] = [];
    const approved: PieceItem[] = [];

    (filteredTasks || []).forEach(p => {
      const hasComments = p.comments && p.comments.length > 0;
      if (p.status === 'REVISION_INTERNA' || p.status === 'REVISION_CLIENTE') {
        pendingApproval.push(p);
        if (hasComments) withChanges.push(p);
      } else if (p.status === 'EN_PRODUCCION' || p.status === 'PENDIENTE') {
        inProduction.push(p);
      } else if (p.status === 'APROBADO' || p.status === 'SUBIDO') {
        approved.push(p);
      }
    });

    return { pendingApproval, inProduction, withChanges, approved };
  }, [filteredTasks]);

  // Rendimiento de Equipo (Admins)
  const teamPerformance = useMemo(() => {
    if (!Array.isArray(users) || !Array.isArray(pieces) || users.length === 0) return [];
    return users.filter(u => u && u.id).map(u => {
      const uPieces = pieces.filter(p =>
        p.assignedToId === u.id ||
        p.assignedTo?.id === u.id ||
        p.createdById === u.id ||
        p.createdBy?.id === u.id ||
        p.processCard?.assignedUserId === u.id
      );

      const totalUnits = countUnits(uPieces);
      const approvedUnits = countUnits(uPieces.filter(p => p.status === 'APROBADO' || p.status === 'SUBIDO'));
      const inProdUnits = countUnits(uPieces.filter(p => p.status === 'EN_PRODUCCION' || p.status === 'PENDIENTE'));
      const carouselSlides = uPieces.filter(p => p.type === 'CARRUSEL').reduce((acc, p) => acc + (Array.isArray(p.files) && p.files.length > 0 ? p.files.length : 1), 0);
      const videosCount = uPieces.filter(p => p.type === 'VIDEO' || p.type === 'REELS').length;
      const staticsCount = uPieces.filter(p => p.type === 'ESTATICA' || !p.type).length;
      const userBrands = Array.from(new Set(uPieces.map(p => p.brand?.name).filter(Boolean)));
      const efficiency = totalUnits > 0 ? Math.round((approvedUnits / totalUnits) * 100) : 100;

      return {
        user: u,
        totalUnits,
        approvedUnits,
        inProdUnits,
        carouselSlides,
        videosCount,
        staticsCount,
        userBrands,
        efficiency
      };
    }).sort((a, b) => b.totalUnits - a.totalUnits);
  }, [users, pieces]);

  // Estados para Módulo de Métricas de Rendimiento Diario y Mensual (Módulo 6 - Control Alejandra & Admin)
  const [perfMonth, setPerfMonth] = useState(new Date().getMonth());
  const [perfYear, setPerfYear] = useState(new Date().getFullYear());
  const [perfSearch, setPerfSearch] = useState('');
  const [selectedPerfCollabId, setSelectedPerfCollabId] = useState<string | null>(null);

  const daysInPerfMonth = useMemo(() => {
    return new Date(perfYear, perfMonth + 1, 0).getDate();
  }, [perfYear, perfMonth]);

  const dailyTeamPerformance = useMemo(() => {
    if (!Array.isArray(users) || !Array.isArray(pieces) || users.length === 0) return [];
    
    const startOfMonth = new Date(Date.UTC(perfYear, perfMonth, 1, 0, 0, 0));
    const endOfMonth = new Date(Date.UTC(perfYear, perfMonth + 1, 1, 0, 0, 0));

    // Filter pieces in this month
    const monthPieces = pieces.filter((p: any) => {
      const pDate = p.updatedAt ? new Date(p.updatedAt) : p.createdAt ? new Date(p.createdAt) : null;
      return pDate && pDate >= startOfMonth && pDate < endOfMonth;
    });

    return users.filter(u => u && u.id && u.role !== 'CLIENTE').map(u => {
      const uPieces = monthPieces.filter((p: any) =>
        p.assignedToId === u.id ||
        p.assignedTo?.id === u.id ||
        p.createdById === u.id ||
        p.createdBy?.id === u.id ||
        p.processCard?.assignedUserId === u.id
      );

      // Overtime for this user in the month
      const uOvertimes = (overtimeRecords || []).filter((r: any) => {
        if (r.userId !== u.id || r.status !== 'APROBADO') return false;
        const d = new Date(r.date);
        return d.getFullYear() === perfYear && d.getMonth() === perfMonth;
      });
      const totalOvertimeHours = uOvertimes.reduce((acc: number, r: any) => acc + (r.hours || 0), 0);
      const totalOvertimeAmount = uOvertimes.reduce((acc: number, r: any) => acc + (r.totalAmount || 0), 0);

      // Vacations for this user in the month
      const uVacations = (vacationRecords || []).filter((r: any) => {
        if (r.userId !== u.id || r.status !== 'APROBADO') return false;
        const s = new Date(r.startDate);
        const e = new Date(r.endDate);
        return (s < endOfMonth && e >= startOfMonth);
      });
      const totalVacationDays = uVacations.reduce((acc: number, r: any) => acc + (r.daysRequested || 0), 0);

      // Day by day mapping
      const daysMap: Record<number, { units: number; piecesCount: number; hasOvertime: boolean; isVacation: boolean; details: string[] }> = {};
      
      for (let day = 1; day <= daysInPerfMonth; day++) {
        const dayDate = new Date(Date.UTC(perfYear, perfMonth, day));
        const dayPieces = uPieces.filter((p: any) => {
          const d = p.updatedAt ? new Date(p.updatedAt) : new Date(p.createdAt);
          return d.getUTCDate() === day;
        });

        const dayUnits = countUnits(dayPieces);
        
        const hasOt = uOvertimes.some((r: any) => {
          const d = new Date(r.date);
          return d.getUTCDate() === day;
        });

        const isVac = uVacations.some((r: any) => {
          const s = new Date(r.startDate);
          const e = new Date(r.endDate);
          return dayDate >= s && dayDate <= e;
        });

        const details = dayPieces.map((p: any) => `${p.title || 'Pieza'} (${p.type || 'ESTATICA'})`);

        daysMap[day] = {
          units: dayUnits,
          piecesCount: dayPieces.length,
          hasOvertime: hasOt,
          isVacation: isVac,
          details
        };
      }

      const totalUnits = countUnits(uPieces);
      const approvedUnits = countUnits(uPieces.filter((p: any) => p.status === 'APROBADO' || p.status === 'SUBIDO'));
      const inProdUnits = countUnits(uPieces.filter((p: any) => p.status === 'EN_PRODUCCION' || p.status === 'PENDIENTE' || p.status === 'REVISION_INTERNA'));
      
      const carouselSlides = uPieces.filter((p: any) => p.type === 'CARRUSEL').reduce((acc, p) => acc + (Array.isArray(p.files) && p.files.length > 0 ? p.files.length : 1), 0);
      const videosCount = uPieces.filter((p: any) => p.type === 'VIDEO' || p.type === 'REELS').length;
      const staticsCount = uPieces.filter((p: any) => p.type === 'ESTATICA' || !p.type).length;
      
      const activeDaysCount = Object.values(daysMap).filter(d => d.units > 0).length;
      const peakDayEntry = Object.entries(daysMap).reduce((max, [day, data]) => data.units > max.units ? { day: parseInt(day), units: data.units } : max, { day: 0, units: 0 });
      
      const monthlyTarget = 30; // base meta mensual
      const complianceRate = Math.min(100, Math.round((totalUnits / monthlyTarget) * 100));
      const effectivenessRate = totalUnits > 0 ? Math.round((approvedUnits / totalUnits) * 100) : 100;
      const dailyAvg = activeDaysCount > 0 ? (totalUnits / activeDaysCount).toFixed(1) : '0';

      return {
        user: u,
        uPieces,
        totalUnits,
        approvedUnits,
        inProdUnits,
        carouselSlides,
        videosCount,
        staticsCount,
        monthlyTarget,
        complianceRate,
        effectivenessRate,
        dailyAvg,
        activeDaysCount,
        peakDay: peakDayEntry,
        totalOvertimeHours,
        totalOvertimeAmount,
        totalVacationDays,
        daysMap
      };
    }).sort((a, b) => b.totalUnits - a.totalUnits);
  }, [users, pieces, overtimeRecords, vacationRecords, perfMonth, perfYear, daysInPerfMonth]);


  const handleOpenInContent = (pieceId: string) => {
    navigate(`/content?pieceId=${pieceId}`, { state: { openPieceId: pieceId } });
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskForm.title.trim() || !newTaskForm.brandId) {
      toast.error('Completa el título y la marca');
      return;
    }
    setSavingTask(true);
    try {
      const res = await api.post('/pieces', {
        title: newTaskForm.title,
        brandId: newTaskForm.brandId,
        type: newTaskForm.type,
        publishDate: newTaskForm.publishDate,
        assignedToId: newTaskForm.assignedToId || (inspectedUser?.id || (user as any)?.id),
        status: 'EN_PRODUCCION'
      });
      setPieces(prev => [res.data, ...prev]);
      setShowNewTaskModal(false);
      setNewTaskForm(prev => ({ ...prev, title: '' }));
      toast.success('✓ Tarea asignada exitosamente');
    } catch {
      toast.error('Error al crear tarea');
    } finally {
      setSavingTask(false);
    }
  };

  // Celdas del Calendario (Misma lógica matemática que Calendar.tsx)
  const cells = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDow = (firstDay.getDay() + 6) % 7; // Lunes = 0
    const totalDays = lastDay.getDate();

    const arr: (number | null)[] = [];
    for (let i = 0; i < startDow; i++) arr.push(null);
    for (let d = 1; d <= totalDays; d++) arr.push(d);
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [month, year]);

  // Filtrado de publicaciones por día para el mes y año seleccionados
  const postsForDay = (d: number) => {
    return (filteredTasks || []).filter(p => {
      if (!p.publishDate) return false;
      try {
        const dt = new Date(p.publishDate);
        return dt.getFullYear() === year && dt.getMonth() === month && dt.getDate() === d;
      } catch {
        return false;
      }
    });
  };

  const totalThisMonth = useMemo(() => {
    return (filteredTasks || []).filter(p => {
      if (!p.publishDate) return false;
      try {
        const dt = new Date(p.publishDate);
        return dt.getFullYear() === year && dt.getMonth() === month;
      } catch {
        return false;
      }
    }).length;
  }, [filteredTasks, month, year]);

  const glassCardStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.72) 0%, rgba(255, 255, 255, 0.42) 100%)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    borderRadius: '22px',
    border: '1px solid rgba(255, 255, 255, 0.75)',
    boxShadow: '0 8px 32px 0 rgba(15, 42, 74, 0.06), inset 0 1px 2px 0 rgba(255, 255, 255, 0.9)',
    transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
  };

  return (
    <div className="zoik-page-container" style={{
      display: 'flex',
      height: '100vh',
      maxHeight: '100vh',
      width: '100vw',
      maxWidth: '100vw',
      background: 'radial-gradient(at 0% 0%, rgba(0, 112, 243, 0.07) 0px, transparent 50%), radial-gradient(at 100% 0%, rgba(38, 136, 201, 0.08) 0px, transparent 50%), radial-gradient(at 100% 100%, rgba(99, 102, 241, 0.06) 0px, transparent 50%), radial-gradient(at 0% 100%, rgba(16, 185, 129, 0.05) 0px, transparent 50%), #edf2f8',
      overflow: 'hidden',
      fontFamily: '"Montserrat", sans-serif'
    }}>
      <Sidebar />

      <main className="zoik-page-main" style={{
        flex: 1,
        height: '100vh',
        maxHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
        minWidth: 0,
        padding: '28px 36px'
      }}>
        
        {/* CABECERA PRINCIPAL */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '22px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 900, color: '#0f2a4a', letterSpacing: '-0.5px' }}>
                {isAdminOrCuentas && !isViewingSpecificUser ? 'Centro de Mando · Workspace Creativo' : `Workspace · ${inspectedUser?.firstName || 'Equipo'}`}
              </h1>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'rgba(15, 42, 74, 0.6)', fontWeight: 600 }}>
              Flujo diario de producción, calendario interactivo y control operativo de marcas.
            </p>
          </div>

          {/* BOTONES DE VISTA & ACCIONES */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div className="liquid-glass-panel" style={{ borderRadius: '14px', padding: '4px', display: 'flex', gap: '4px' }}>
              <button
                onClick={() => setMainView('FLOW')}
                style={{
                  padding: '7px 16px',
                  borderRadius: '10px',
                  border: 'none',
                  background: mainView === 'FLOW' ? 'linear-gradient(135deg, #0070f3 0%, #005bb5 100%)' : 'transparent',
                  color: mainView === 'FLOW' ? '#ffffff' : '#0f2a4a',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: '"Montserrat", sans-serif'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><IconBolt size={14} /> Flujo Diario</span>
              </button>
              <button
                onClick={() => setMainView('CALENDAR')}
                style={{
                  padding: '7px 16px',
                  borderRadius: '10px',
                  border: 'none',
                  background: mainView === 'CALENDAR' ? 'linear-gradient(135deg, #0070f3 0%, #005bb5 100%)' : 'transparent',
                  color: mainView === 'CALENDAR' ? '#ffffff' : '#0f2a4a',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: '"Montserrat", sans-serif'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><IconCalendar size={14} /> Calendario</span>
              </button>
              {isAdminOrCuentas && (
                <button
                  onClick={() => setMainView('PERFORMANCE')}
                  style={{
                    padding: '7px 16px',
                    borderRadius: '10px',
                    border: 'none',
                    background: mainView === 'PERFORMANCE' ? 'linear-gradient(135deg, #0070f3 0%, #005bb5 100%)' : 'transparent',
                    color: mainView === 'PERFORMANCE' ? '#ffffff' : '#0f2a4a',
                    fontSize: '12px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><IconTrophy size={14} /> Rendimiento</span>
                </button>
              )}
              {isAdminOrCuentas && (
                <button
                  onClick={() => setMainView('COLLABORATORS')}
                  style={{
                    padding: '7px 16px',
                    borderRadius: '10px',
                    border: 'none',
                    background: mainView === 'COLLABORATORS' ? 'linear-gradient(135deg, #0070f3 0%, #005bb5 100%)' : 'transparent',
                    color: mainView === 'COLLABORATORS' ? '#ffffff' : '#0f2a4a',
                    fontSize: '12px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <IconUsers size={14} /> Colaboradores
                    {collaborators.filter(c => c.contractStatus === 'POR_VENCER').length > 0 && (
                      <span style={{
                        background: '#ef4444',
                        color: '#fff',
                        borderRadius: '10px',
                        padding: '1px 6px',
                        fontSize: '10px',
                        fontWeight: 900,
                        marginLeft: '4px'
                      }}>
                        {collaborators.filter(c => c.contractStatus === 'POR_VENCER').length}
                      </span>
                    )}
                  </span>
                </button>
              )}
              {canManageOvertime && (
                <button
                  onClick={() => { setMainView('OVERTIME'); loadOvertimeData(); }}
                  style={{
                    padding: '7px 16px',
                    borderRadius: '10px',
                    border: 'none',
                    background: mainView === 'OVERTIME' ? 'linear-gradient(135deg, #0070f3 0%, #005bb5 100%)' : 'transparent',
                    color: mainView === 'OVERTIME' ? '#ffffff' : '#0f2a4a',
                    fontSize: '12px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <IconClock size={14} /> Horas Extras
                    {overtimeStats.pendingCount > 0 && (
                      <span style={{
                        background: '#f59e0b',
                        color: '#fff',
                        borderRadius: '10px',
                        padding: '1px 6px',
                        fontSize: '10px',
                        fontWeight: 900,
                        marginLeft: '4px'
                      }}>
                        {overtimeStats.pendingCount}
                      </span>
                    )}
                  </span>
                </button>
              )}
              <button
                onClick={() => navigate('/permissions')}
                style={{
                  padding: '7px 16px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #0070f3 0%, #0f2a4a 100%)',
                  color: '#ffffff',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(0, 112, 243, 0.25)',
                  fontFamily: '"Montserrat", sans-serif'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <IconCalendarDays size={14} color="white" /> Permisos y Horas Extra
                </span>
              </button>
              <button
                onClick={() => { setMainView('VACATIONS'); loadVacationData(); }}
                style={{
                  padding: '7px 16px',
                  borderRadius: '10px',
                  border: 'none',
                  background: mainView === 'VACATIONS' ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : 'transparent',
                  color: mainView === 'VACATIONS' ? '#ffffff' : '#0f2a4a',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: '"Montserrat", sans-serif'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  🌴 Vacaciones
                  {vacationStats.pendingCount > 0 && (isAlejandra || isAdminOrCuentas) && (
                    <span style={{
                      background: '#ec4899',
                      color: '#fff',
                      borderRadius: '10px',
                      padding: '1px 6px',
                      fontSize: '10px',
                      fontWeight: 900,
                      marginLeft: '4px'
                    }}>
                      {vacationStats.pendingCount}
                    </span>
                  )}
                </span>
              </button>
            </div>

            <button
              onClick={() => {
                const today = new Date().toISOString().split('T')[0];
                const end = new Date(Date.now() + 15 * 86400000).toISOString().split('T')[0];
                setRequestVacationForm(prev => ({
                  ...prev,
                  startDate: today,
                  endDate: end,
                  daysRequested: 15
                }));
                setShowRequestVacationModal(true);
              }}
              style={{
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                border: 'none',
                borderRadius: '14px',
                padding: '10px 16px',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 800,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: '"Montserrat", sans-serif',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span>🌴 Radicar Vacaciones</span>
            </button>

            <button
              onClick={() => setShowRegisterOvertimeModal(true)}
              style={{
                background: 'linear-gradient(135deg, #0070f3 0%, #0056d6 100%)',
                border: 'none',
                borderRadius: '14px',
                padding: '10px 16px',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 800,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: '"Montserrat", sans-serif',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <IconClock size={14} color="#ffffff" /> Registrar Horas Extras
            </button>

            <button
              onClick={() => setShowNewTaskModal(true)}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: 'none',
                borderRadius: '14px',
                padding: '10px 18px',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 800,
                cursor: 'pointer',
                boxShadow: 'none',
                transition: 'all 0.2s ease',
                fontFamily: '"Montserrat", sans-serif'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 18px rgba(16, 185, 129, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><IconPlus size={15} color="#ffffff" /> Asignar Tarea</span>
            </button>

            {isAdminOrCuentas && (
              <select
                value={selectedUserId}
                onChange={e => setSelectedUserId(e.target.value)}
                className="liquid-glass-item"
                style={{
                  padding: '9px 16px',
                  borderRadius: '14px',
                  color: '#0f2a4a',
                  fontWeight: 800,
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontFamily: '"Montserrat", sans-serif',
                  outline: 'none'
                }}
              >
                <option value="MY_WORKSPACE">🔥 Aprobaciones de Mis Marcas</option>
                <optgroup label="Colaboradores">
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.firstName} {u.lastName} ({u.role})
                    </option>
                  ))}
                </optgroup>
              </select>
            )}
          </div>
        </div>

        {/* FICHA DEL COLABORADOR EN LIQUID GLASS */}
        <div
          className="liquid-glass-panel"
          style={{
            padding: '22px 28px',
            marginBottom: '22px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '18px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
            <div style={{ position: 'relative', width: '56px', height: '56px', flexShrink: 0 }}>
              {(() => {
                const realAvatar = inspectedUser?.avatar || users.find(u => u.id === inspectedUser?.id)?.avatar || (user as any)?.avatar;
                return (
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #0070f3 0%, #005bb5 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    fontWeight: 900,
                    overflow: 'hidden',
                    boxShadow: '0 4px 14px rgba(0, 112, 243, 0.25)',
                    border: '1.5px solid rgba(255,255,255,0.8)'
                  }}>
                    {realAvatar ? <img src={realAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (inspectedUser?.firstName?.[0] || 'U')}
                  </div>
                );
              })()}
              <span className="pulse-online" style={{
                position: 'absolute',
                bottom: '-2px',
                right: '-2px',
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                background: '#10b981',
                border: '2.5px solid white'
              }} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 900, color: '#0f2a4a', letterSpacing: '-0.3px' }}>
                  {inspectedUser?.firstName} {inspectedUser?.lastName}
                </h2>
                <span style={{
                  fontSize: '10px',
                  fontWeight: 800,
                  padding: '3px 10px',
                  borderRadius: '8px',
                  background: 'rgba(0, 112, 243, 0.12)',
                  color: '#0070f3',
                  border: '1px solid rgba(0, 112, 243, 0.25)'
                }}>
                  {inspectedUser?.role || 'CREATIVO'}
                </span>
                <button
                  onClick={() => navigate('/chat', { state: { targetUserId: inspectedUser?.id } })}
                  title={`Enviar mensaje a ${inspectedUser?.firstName || 'colaborador'}`}
                  style={{
                    background: 'rgba(0, 112, 243, 0.08)',
                    border: '1px solid rgba(0, 112, 243, 0.2)',
                    borderRadius: '8px',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: '#0070f3',
                    padding: 0,
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 112, 243, 0.18)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 112, 243, 0.08)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <IconMessage size={15} color="#0070f3" />
                </button>
              </div>
              <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, marginTop: '2px' }}>
                {inspectedUser?.email || ''}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '10px', fontWeight: 800, color: 'rgba(15,42,74,0.5)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Marcas Asignadas:</span>
                {Array.from(new Set((filteredTasks || []).map(t => t.brand?.name).filter(Boolean))).slice(0, 6).map(bName => (
                  <span key={bName} style={{
                    fontSize: '10px',
                    fontWeight: 800,
                    background: 'rgba(255, 255, 255, 0.75)',
                    color: '#0f2a4a',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    border: '1px solid rgba(15, 42, 74, 0.08)'
                  }}>
                    {bName}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.5)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Total Volumen Producido
            </div>
            <div style={{ fontSize: '32px', fontWeight: 900, color: '#0f2a4a', lineHeight: 1.1, marginTop: '2px', letterSpacing: '-1px' }}>
              <RouletteNumber value={countUnits(filteredTasks)} /> <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 700 }}>unidades</span>
            </div>
            <div style={{ fontSize: '11px', color: '#0070f3', fontWeight: 700, marginTop: '4px' }}>
              {(filteredTasks || []).filter(p => p.type === 'CARRUSEL').reduce((acc, p) => acc + (Array.isArray(p.files) && p.files.length > 0 ? p.files.length : 1), 0)} slides • {(filteredTasks || []).filter(p => p.type === 'VIDEO' || p.type === 'REELS').length} videos • {(filteredTasks || []).filter(p => p.type === 'ESTATICA' || !p.type).length} estáticas
            </div>
          </div>
        </div>

        {/* 2. TARJETAS KPIS TRASLÚCIDAS (GLASSMORPHISM PURO - IDÉNTICO A DASHBOARD) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '18px', marginBottom: '32px' }}>
          {/* KPI 1: Por Aprobar */}
          <div
            style={{ ...glassCardStyle, padding: '22px 24px' }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 12px 36px 0 rgba(15, 42, 74, 0.1), inset 0 1px 2px 0 rgba(255, 255, 255, 0.95)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = glassCardStyle.boxShadow as string;
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.55)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                Por Aprobar
              </span>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '12px',
                background: 'rgba(239, 246, 255, 0.85)',
                border: '1px solid rgba(191, 219, 254, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <IconClock size={18} color="#0f2a4a" />
              </div>
            </div>
            <div style={{ fontSize: '38px', fontWeight: 900, color: '#0f2a4a', lineHeight: 1, letterSpacing: '-1.5px', display: 'flex', alignItems: 'center' }}>
              <RouletteNumber value={countUnits(classifiedTasks.pendingApproval)} />
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, marginTop: '8px' }}>
              Revisión activa ({classifiedTasks.pendingApproval.length} publicaciones)
            </div>
          </div>

          {/* KPI 2: En Producción */}
          <div
            style={{ ...glassCardStyle, padding: '22px 24px' }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 12px 36px 0 rgba(15, 42, 74, 0.1), inset 0 1px 2px 0 rgba(255, 255, 255, 0.95)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = glassCardStyle.boxShadow as string;
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.55)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                En Producción
              </span>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '12px',
                background: 'rgba(239, 246, 255, 0.85)',
                border: '1px solid rgba(191, 219, 254, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <IconLayers size={18} color="#0f2a4a" />
              </div>
            </div>
            <div style={{ fontSize: '38px', fontWeight: 900, color: '#0f2a4a', lineHeight: 1, letterSpacing: '-1.5px', display: 'flex', alignItems: 'center' }}>
              <RouletteNumber value={countUnits(classifiedTasks.inProduction)} />
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, marginTop: '8px' }}>
              En mesa de trabajo ({classifiedTasks.inProduction.length} publicaciones)
            </div>
          </div>

          {/* KPI 3: Con Comentarios */}
          <div
            style={{ ...glassCardStyle, padding: '22px 24px' }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 12px 36px 0 rgba(15, 42, 74, 0.1), inset 0 1px 2px 0 rgba(255, 255, 255, 0.95)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = glassCardStyle.boxShadow as string;
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.55)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                Con Comentarios
              </span>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '12px',
                background: 'rgba(239, 246, 255, 0.85)',
                border: '1px solid rgba(191, 219, 254, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <IconMessage size={18} color="#0f2a4a" />
              </div>
            </div>
            <div style={{ fontSize: '38px', fontWeight: 900, color: '#0f2a4a', lineHeight: 1, letterSpacing: '-1.5px', display: 'flex', alignItems: 'center' }}>
              <RouletteNumber value={countUnits(classifiedTasks.withChanges)} />
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, marginTop: '8px' }}>
              Requieren corrección ({classifiedTasks.withChanges.length} publicaciones)
            </div>
          </div>

          {/* KPI 4: Aprobadas Este Mes */}
          <div
            style={{ ...glassCardStyle, padding: '22px 24px' }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 12px 36px 0 rgba(15, 42, 74, 0.1), inset 0 1px 2px 0 rgba(255, 255, 255, 0.95)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = glassCardStyle.boxShadow as string;
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.55)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                Aprobadas Este Mes
              </span>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '12px',
                background: 'rgba(239, 246, 255, 0.85)',
                border: '1px solid rgba(191, 219, 254, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <IconCheckCircle size={18} color="#0f2a4a" />
              </div>
            </div>
            <div style={{ fontSize: '38px', fontWeight: 900, color: '#0f2a4a', lineHeight: 1, letterSpacing: '-1.5px', display: 'flex', alignItems: 'center' }}>
              <RouletteNumber value={countUnits(classifiedTasks.approved)} />
            </div>
            <div style={{ fontSize: '11px', color: '#16a34a', fontWeight: 700, marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a' }}></span>
              Listas para entrega ({classifiedTasks.approved.length} publicaciones)
            </div>
          </div>
        </div>

        {/* VISTA 1: FLUJO DE TRABAJO */}
        {mainView === 'FLOW' && (
          <div className="liquid-glass-panel" style={{ padding: '26px 30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div style={{ fontSize: '17px', fontWeight: 900, color: '#0f2a4a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IconBolt size={18} color="#0070f3" />
                <span>Cola de Tareas Asignadas ({filteredTasks.length} publicaciones)</span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filteredTasks.length === 0 ? (
                <div style={{ padding: '50px 20px', textAlign: 'center', color: '#94a3b8', fontSize: '14px', fontWeight: 600 }}>
                  🎉 No hay tareas pendientes en este workspace.
                </div>
              ) : (
                filteredTasks.map(t => (
                  <div
                    key={t.id}
                    onClick={() => handleOpenInContent(t.id)}
                    className="liquid-glass-item"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 20px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: t.type === 'CARRUSEL' ? 'rgba(139, 92, 246, 0.12)' : t.type === 'VIDEO' ? 'rgba(236, 72, 153, 0.12)' : 'rgba(0, 112, 243, 0.12)',
                        border: `1px solid ${t.type === 'CARRUSEL' ? 'rgba(139, 92, 246, 0.25)' : t.type === 'VIDEO' ? 'rgba(236, 72, 153, 0.25)' : 'rgba(0, 112, 243, 0.25)'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {t.type === 'CARRUSEL' ? (
                          <IconLayers size={18} color="#8b5cf6" />
                        ) : t.type === 'VIDEO' ? (
                          <IconVideo size={18} color="#ec4899" />
                        ) : (
                          <IconImage size={18} color="#0070f3" />
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f2a4a' }}>{t.title}</div>
                        <div style={{ fontSize: '12px', color: '#0070f3', fontWeight: 700, marginTop: '2px' }}>
                          {t.brand?.name || 'Marca'} {t.type === 'CARRUSEL' ? `• ${Array.isArray(t.files) && t.files.length > 0 ? t.files.length : 1} slides` : ''}
                        </div>
                      </div>
                    </div>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 800,
                      padding: '5px 12px',
                      borderRadius: '8px',
                      background: t.status === 'APROBADO' || t.status === 'SUBIDO' ? 'rgba(16, 185, 129, 0.12)' : t.status === 'REVISION_INTERNA' || t.status === 'REVISION_CLIENTE' ? 'rgba(245, 158, 11, 0.12)' : 'rgba(15, 42, 74, 0.08)',
                      color: t.status === 'APROBADO' || t.status === 'SUBIDO' ? '#10b981' : t.status === 'REVISION_INTERNA' || t.status === 'REVISION_CLIENTE' ? '#d97706' : '#0f2a4a',
                      border: `1px solid ${t.status === 'APROBADO' || t.status === 'SUBIDO' ? 'rgba(16, 185, 129, 0.25)' : t.status === 'REVISION_INTERNA' || t.status === 'REVISION_CLIENTE' ? 'rgba(245, 158, 11, 0.25)' : 'rgba(15, 42, 74, 0.1)'}`
                    }}>
                      {t.status}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* VISTA 2: CALENDARIO EXACTO DE CALENDAR.TSX (LIQUID GLASSMORPHISM) */}
        {mainView === 'CALENDAR' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {/* Navigation Card */}
            <div
              className="liquid-glass-panel"
              style={{
                padding: '12px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => {
                    if (month === 0) {
                      setMonth(11);
                      setYear(y => y - 1);
                    } else setMonth(m => m - 1);
                  }}
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '10px',
                    border: '1px solid rgba(15, 42, 74, 0.1)',
                    background: 'rgba(255, 255, 255, 0.75)',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#0f2a4a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'none',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'white'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255, 255, 255, 0.75)'; }}
                >
                  ‹
                </button>

                <div style={{
                  fontSize: '17px',
                  fontWeight: 800,
                  color: '#0f2a4a',
                  minWidth: '200px',
                  textAlign: 'center',
                  letterSpacing: '-0.2px'
                }}>
                  {MONTHS[month]} <span style={{ color: '#0070f3' }}>{year}</span>
                </div>

                <button
                  onClick={() => {
                    if (month === 11) {
                      setMonth(0);
                      setYear(y => y + 1);
                    } else setMonth(m => m + 1);
                  }}
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '10px',
                    border: '1px solid rgba(15, 42, 74, 0.1)',
                    background: 'rgba(255, 255, 255, 0.75)',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#0f2a4a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'none',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'white'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255, 255, 255, 0.75)'; }}
                >
                  ›
                </button>

                <button
                  onClick={() => {
                    setYear(now.getFullYear());
                    setMonth(now.getMonth());
                  }}
                  style={{
                    marginLeft: '8px',
                    padding: '6px 14px',
                    borderRadius: '10px',
                    border: '1px solid rgba(0, 112, 243, 0.25)',
                    background: 'rgba(0, 112, 243, 0.08)',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    color: '#0070f3',
                    fontFamily: '"Montserrat", sans-serif',
                    boxShadow: 'none',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0, 112, 243, 0.15)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0, 112, 243, 0.08)'; }}
                  onMouseDown={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(0.97)'; }}
                  onMouseUp={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
                >
                  Hoy
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  padding: '6px 14px',
                  borderRadius: '10px',
                  background: 'rgba(15, 42, 74, 0.04)',
                  border: '1px solid rgba(15, 42, 74, 0.06)',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: 'rgba(15, 42, 74, 0.75)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <IconCalendar size={14} color="#0070f3" />
                  <span><strong style={{ color: '#0f2a4a' }}>{totalThisMonth}</strong> publicaciones este mes</span>
                </div>
              </div>
            </div>

            {/* Main Calendar Grid Card */}
            <div
              className="liquid-glass-panel"
              style={{
                overflow: 'hidden',
                padding: '0',
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              {/* Day Headers */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.6) 0%, rgba(240, 245, 252, 0.4) 100%)',
                borderBottom: '1px solid rgba(15, 42, 74, 0.06)',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                {DAYS.map(d => (
                  <div key={d} style={{
                    padding: '12px 6px',
                    textAlign: 'center',
                    fontSize: '11px',
                    fontWeight: 800,
                    color: '#0f2a4a',
                    letterSpacing: '0.8px',
                    textTransform: 'uppercase',
                    opacity: 0.75,
                    minWidth: 0,
                    overflow: 'hidden',
                    boxSizing: 'border-box'
                  }}>
                    {d}
                  </div>
                ))}
              </div>

              {/* Grid Cells */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                {cells.map((d, i) => {
                  if (!d) {
                    return (
                      <div
                        key={'empty-' + i}
                        style={{
                          minHeight: '142px',
                          background: 'rgba(241, 245, 249, 0.25)',
                          borderRight: '1px solid rgba(15, 42, 74, 0.05)',
                          borderBottom: '1px solid rgba(15, 42, 74, 0.05)',
                          minWidth: 0,
                          boxSizing: 'border-box'
                        }}
                      />
                    );
                  }

                  const todayD = now.getFullYear() === year && now.getMonth() === month && now.getDate() === d;
                  const dayPosts = postsForDay(d);

                  return (
                    <div
                      key={d}
                      style={{
                        minHeight: '142px',
                        borderRight: '1px solid rgba(15, 42, 74, 0.05)',
                        borderBottom: '1px solid rgba(15, 42, 74, 0.05)',
                        padding: '8px 6px',
                        transition: 'all 0.15s ease',
                        background: todayD ? 'rgba(0, 112, 243, 0.03)' : 'transparent',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        minWidth: 0,
                        overflow: 'hidden',
                        boxSizing: 'border-box'
                      }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLDivElement).style.background = todayD
                          ? 'rgba(0, 112, 243, 0.06)'
                          : 'rgba(255, 255, 255, 0.8)';
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLDivElement).style.background = todayD
                          ? 'rgba(0, 112, 243, 0.03)'
                          : 'transparent';
                      }}
                    >
                      {/* Day number header */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '6px',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}>
                        <div style={{
                          width: '26px',
                          height: '26px',
                          borderRadius: '8px',
                          background: todayD ? '#0070f3' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: 'none'
                        }}>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: todayD ? 800 : 700,
                            color: todayD ? '#ffffff' : 'rgba(15, 42, 74, 0.75)'
                          }}>
                            {d}
                          </span>
                        </div>

                        {dayPosts.length > 0 && (
                          <span style={{
                            fontSize: '10px',
                            fontWeight: 700,
                            color: '#0070f3',
                            background: 'rgba(0, 112, 243, 0.08)',
                            padding: '1px 6px',
                            borderRadius: '6px',
                            boxShadow: 'none'
                          }}>
                            {dayPosts.length}
                          </span>
                        )}
                      </div>

                      {/* Liquid Glass Post Chips */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, padding: '2px 0', width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                        {dayPosts.slice(0, 3).map(p => {
                          const bStyle = getBrandStyle(p.brand?.name);

                          return (
                            <div
                              key={p.id}
                              onClick={e => {
                                e.stopPropagation();
                                handleOpenInContent(p.id);
                              }}
                              style={{
                                padding: '8px 9px',
                                borderRadius: '11px',
                                background: bStyle.bg,
                                backdropFilter: 'blur(16px) saturate(180%)',
                                WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                                border: 'none',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                transition: 'all 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
                                boxShadow: `0 3px 10px 0 ${bStyle.glow}, inset 0 1.5px 2px 0 rgba(255, 255, 255, 0.95)`,
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '6px',
                                userSelect: 'none',
                                width: '100%',
                                minWidth: 0,
                                boxSizing: 'border-box'
                              }}
                              onMouseEnter={e => {
                                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                                (e.currentTarget as HTMLDivElement).style.boxShadow = `0 8px 18px -2px ${bStyle.glow}, inset 0 2px 3px 0 rgba(255, 255, 255, 1)`;
                              }}
                              onMouseLeave={e => {
                                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                                (e.currentTarget as HTMLDivElement).style.boxShadow = `0 3px 10px 0 ${bStyle.glow}, inset 0 1.5px 2px 0 rgba(255, 255, 255, 0.95)`;
                              }}
                              onMouseDown={e => {
                                (e.currentTarget as HTMLDivElement).style.transform = 'scale(0.98)';
                              }}
                              onMouseUp={e => {
                                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                              }}
                            >
                              {/* Left Text */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flex: 1, overflow: 'hidden' }}>
                                <div style={{
                                  fontSize: '11px',
                                  fontWeight: 800,
                                  color: bStyle.color,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  letterSpacing: '-0.2px',
                                  lineHeight: 1.25
                                }}>
                                  {p.brand?.name || 'Marca'}
                                </div>
                                <div style={{
                                  fontSize: '9.5px',
                                  fontWeight: 600,
                                  color: 'rgba(15, 42, 74, 0.74)',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  lineHeight: 1.25
                                }}>
                                  {p.title || 'Sin título'}
                                </div>
                              </div>

                              {/* Right Format Icon */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                <span style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'rgba(255, 255, 255, 0.7)',
                                  borderRadius: '6px',
                                  padding: '2px 4px'
                                }}>
                                  {p.type === 'CARRUSEL' ? (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '9px', fontWeight: 800, color: '#8b5cf6' }}>
                                      <IconLayers size={11} color="#8b5cf6" />
                                      {Array.isArray(p.files) && p.files.length > 0 ? p.files.length : 1}
                                    </span>
                                  ) : p.type === 'VIDEO' ? (
                                    <IconVideo size={11} color="#ec4899" />
                                  ) : (
                                    <IconImage size={11} color="#0070f3" />
                                  )}
                                </span>
                              </div>
                            </div>
                          );
                        })}

                        {dayPosts.length > 3 && (
                          <div style={{
                            textAlign: 'center',
                            fontSize: '9px',
                            fontWeight: 700,
                            color: '#0070f3',
                            padding: '2px 0'
                          }}>
                            +{dayPosts.length - 3} más
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* VISTA 3: SUBMÓDULO DE RENDIMIENTO (ADMINS) */}
        {/* VISTA 3: SUBMÓDULO DE RENDIMIENTO DÍA A DÍA Y CONTROL MENSUAL (MÓDULO 6 - ALEJANDRA & ADMIN) */}
        {mainView === 'PERFORMANCE' && isAdminOrCuentas && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
            
            {/* PANEL DE CONTROL SUPERIOR CON SELECTOR DE MES/AÑO Y EXPORTACIÓN */}
            <div className="liquid-glass-card" style={{ padding: '22px 26px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, #0070f3 0%, #005bb5 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff' }}>
                      <IconTrophy size={20} />
                    </div>
                    <div>
                      <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#0f2a4a' }}>
                        Métricas de Rendimiento Diario & Control Mensual
                      </h2>
                      <p style={{ margin: '3px 0 0', fontSize: '12px', color: 'rgba(15,42,74,0.6)', fontWeight: 600 }}>
                        Seguimiento día a día de entregas, efectividad, cumplimiento de metas, horas extras y vacaciones por colaborador
                      </p>
                    </div>
                  </div>
                </div>

                {/* SELECTORES DE MES Y AÑO */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      if (perfMonth === 0) { setPerfMonth(11); setPerfYear(prev => prev - 1); }
                      else { setPerfMonth(prev => prev - 1); }
                    }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '10px',
                      border: '1px solid rgba(15,42,74,0.15)',
                      background: '#ffffff',
                      color: '#0f2a4a',
                      fontWeight: 800,
                      cursor: 'pointer'
                    }}
                  >
                    ‹
                  </button>

                  <select
                    value={perfMonth}
                    onChange={(e) => setPerfMonth(parseInt(e.target.value))}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '10px',
                      border: '1px solid rgba(15,42,74,0.15)',
                      background: '#ffffff',
                      color: '#0f2a4a',
                      fontSize: '12px',
                      fontWeight: 800,
                      outline: 'none',
                      fontFamily: '"Montserrat", sans-serif'
                    }}
                  >
                    {MONTHS.map((m, idx) => (
                      <option key={m} value={idx}>{m}</option>
                    ))}
                  </select>

                  <select
                    value={perfYear}
                    onChange={(e) => setPerfYear(parseInt(e.target.value))}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '10px',
                      border: '1px solid rgba(15,42,74,0.15)',
                      background: '#ffffff',
                      color: '#0f2a4a',
                      fontSize: '12px',
                      fontWeight: 800,
                      outline: 'none',
                      fontFamily: '"Montserrat", sans-serif'
                    }}
                  >
                    {[2025, 2026, 2027].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>

                  <button
                    onClick={() => {
                      if (perfMonth === 11) { setPerfMonth(0); setPerfYear(prev => prev + 1); }
                      else { setPerfMonth(prev => prev + 1); }
                    }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '10px',
                      border: '1px solid rgba(15,42,74,0.15)',
                      background: '#ffffff',
                      color: '#0f2a4a',
                      fontWeight: 800,
                      cursor: 'pointer'
                    }}
                  >
                    ›
                  </button>

                  <button
                    onClick={() => {
                      setPerfMonth(new Date().getMonth());
                      setPerfYear(new Date().getFullYear());
                    }}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '10px',
                      border: '1px solid rgba(0,112,243,0.3)',
                      background: 'rgba(0,112,243,0.06)',
                      color: '#0070f3',
                      fontSize: '12px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      fontFamily: '"Montserrat", sans-serif'
                    }}
                  >
                    📅 Mes Actual
                  </button>

                  <button
                    onClick={() => window.print()}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '10px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                      color: '#ffffff',
                      fontSize: '12px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: '0 4px 12px rgba(2,132,199,0.25)',
                      fontFamily: '"Montserrat", sans-serif'
                    }}
                  >
                    <IconDownload size={14} /> Exportar / Imprimir Informe
                  </button>
                </div>
              </div>

              {/* KPI BANNERS ROW */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginTop: '20px' }}>
                <div style={{ background: 'rgba(0,112,243,0.05)', borderRadius: '14px', padding: '16px', border: '1px solid rgba(0,112,243,0.15)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase' }}>
                    Entregables del Mes ({MONTHS[perfMonth]})
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 900, color: '#0070f3', marginTop: '6px' }}>
                    {dailyTeamPerformance.reduce((acc, c) => acc + c.totalUnits, 0)} unidades
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(15,42,74,0.5)', marginTop: '2px' }}>
                    {dailyTeamPerformance.reduce((acc, c) => acc + c.approvedUnits, 0)} aprobadas • {dailyTeamPerformance.reduce((acc, c) => acc + c.inProdUnits, 0)} en producción
                  </div>
                </div>

                <div style={{ background: 'rgba(16,185,129,0.05)', borderRadius: '14px', padding: '16px', border: '1px solid rgba(16,185,129,0.15)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase' }}>
                    Efectividad & Calidad Global
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 900, color: '#059669', marginTop: '6px' }}>
                    {dailyTeamPerformance.reduce((acc, c) => acc + c.totalUnits, 0) > 0
                      ? Math.round((dailyTeamPerformance.reduce((acc, c) => acc + c.approvedUnits, 0) / dailyTeamPerformance.reduce((acc, c) => acc + c.totalUnits, 0)) * 100)
                      : 100}%
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(15,42,74,0.5)', marginTop: '2px' }}>
                    Tasa de aprobación directa en entregas
                  </div>
                </div>

                <div style={{ background: 'rgba(139,92,246,0.05)', borderRadius: '14px', padding: '16px', border: '1px solid rgba(139,92,246,0.15)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase' }}>
                    Horas Extras Auditadas
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 900, color: '#7c3aed', marginTop: '6px' }}>
                    {dailyTeamPerformance.reduce((acc, c) => acc + c.totalOvertimeHours, 0)} horas
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(15,42,74,0.5)', marginTop: '2px' }}>
                    ${dailyTeamPerformance.reduce((acc, c) => acc + c.totalOvertimeAmount, 0).toLocaleString('es-CO')} COP en nómina
                  </div>
                </div>

                <div style={{ background: 'rgba(2,132,199,0.05)', borderRadius: '14px', padding: '16px', border: '1px solid rgba(2,132,199,0.15)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase' }}>
                    Días de Vacaciones Gozados
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 900, color: '#0284c7', marginTop: '6px' }}>
                    {dailyTeamPerformance.reduce((acc, c) => acc + c.totalVacationDays, 0)} días
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(15,42,74,0.5)', marginTop: '2px' }}>
                    Descanso remunerado en {MONTHS[perfMonth]}
                  </div>
                </div>
              </div>
            </div>

            {/* AUDITORÍA Y CENTRO DE MANDO PARA ALEJANDRA FERNÁNDEZ */}
            {isAlejandra && (
              <div className="liquid-glass-card" style={{ padding: '20px 24px', borderLeft: '4px solid #0070f3' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'rgba(0,112,243,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                    💼
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 900, color: '#0f2a4a' }}>
                      Centro de Supervisión Contable & Nómina — Alejandra Fernández
                    </h3>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(15,42,74,0.7)', lineHeight: 1.5 }}>
                      En este panel supervisas día a día el rendimiento real de cada tripulante, correlacionando las horas extras registradas y las vacaciones gozadas con el volumen de entregables completados. La aprobación o rechazo operativo de piezas operativas se encuentra centralizada en Dirección y Producción.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* MATRIZ DE RENDIMIENTO DÍA A DÍA POR COLABORADOR */}
            <div className="liquid-glass-card" style={{ padding: '24px 26px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 900, color: '#0f2a4a' }}>
                    Matriz Día a Día de Producción & Desempeño ({MONTHS[perfMonth]} {perfYear})
                  </h3>
                  <p style={{ margin: '3px 0 0', fontSize: '12px', color: 'rgba(15,42,74,0.6)' }}>
                    Visualiza la cadencia diaria (días 1 al {daysInPerfMonth}) con conteo de unidades, horas extras y vacaciones
                  </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="Buscar tripulante..."
                      value={perfSearch}
                      onChange={(e) => setPerfSearch(e.target.value)}
                      style={{
                        padding: '7px 12px 7px 30px',
                        borderRadius: '10px',
                        border: '1px solid rgba(15,42,74,0.15)',
                        background: 'rgba(255,255,255,0.8)',
                        fontSize: '12px',
                        outline: 'none',
                        fontFamily: '"Montserrat", sans-serif',
                        width: '180px'
                      }}
                    />
                    <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(15,42,74,0.4)', pointerEvents: 'none' }}>
                      <IconSearch size={14} />
                    </div>
                  </div>

                  {/* Leyenda de la matriz */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11px', color: 'rgba(15,42,74,0.7)', marginLeft: '10px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#059669' }}></span> 3+ unids
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#0284c7' }}></span> 1-2 unids
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>⚡</span> Horas extras
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span>🌴</span> Vacaciones
                    </span>
                  </div>
                </div>
              </div>

              {/* LISTA DE TARJETAS POR COLABORADOR */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {dailyTeamPerformance
                  .filter(c => {
                    if (!perfSearch) return true;
                    const s = perfSearch.toLowerCase();
                    return (
                      (c.user.firstName || '').toLowerCase().includes(s) ||
                      (c.user.lastName || '').toLowerCase().includes(s) ||
                      (c.user.position || '').toLowerCase().includes(s) ||
                      (c.user.role || '').toLowerCase().includes(s)
                    );
                  })
                  .map((c, idx) => (
                    <div
                      key={c.user.id}
                      style={{
                        background: 'rgba(255,255,255,0.75)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '16px',
                        border: '1px solid rgba(15,42,74,0.08)',
                        padding: '18px 20px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '14px',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 4px 14px rgba(15,42,74,0.04)'
                      }}
                    >
                      {/* Cabecera del colaborador */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '42px',
                            height: '42px',
                            borderRadius: '12px',
                            background: idx === 0 ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'linear-gradient(135deg, #0070f3 0%, #005bb5 100%)',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 900,
                            fontSize: '15px'
                          }}>
                            {c.user.firstName?.charAt(0)}{c.user.lastName?.charAt(0)}
                          </div>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '15px', fontWeight: 900, color: '#0f2a4a' }}>
                                {c.user.firstName} {c.user.lastName}
                              </span>
                              {idx === 0 && (
                                <span style={{ background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 800 }}>
                                  🌟 Mayor Producción
                                </span>
                              )}
                              <span style={{ background: 'rgba(15,42,74,0.06)', color: 'rgba(15,42,74,0.7)', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 700 }}>
                                {c.user.position || c.user.role}
                              </span>
                            </div>
                            <div style={{ fontSize: '11px', color: 'rgba(15,42,74,0.6)', marginTop: '2px' }}>
                              Salario Base: ${(c.user as any)?.baseSalary ? `${((c.user as any).baseSalary).toLocaleString('es-CO')} COP` : 'Sin registrar'} • {c.carouselSlides} diaps • {c.videosCount} vid • {c.staticsCount} est
                            </div>
                          </div>
                        </div>

                        {/* Badges de Métricas */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                          <div style={{ textAlign: 'center', padding: '6px 12px', borderRadius: '10px', background: 'rgba(0,112,243,0.08)' }}>
                            <div style={{ fontSize: '14px', fontWeight: 900, color: '#0070f3' }}>
                              {c.totalUnits} / {c.monthlyTarget}
                            </div>
                            <div style={{ fontSize: '10px', fontWeight: 800, color: 'rgba(15,42,74,0.6)' }}>
                              Meta ({c.complianceRate}%)
                            </div>
                          </div>

                          <div style={{ textAlign: 'center', padding: '6px 12px', borderRadius: '10px', background: c.effectivenessRate >= 85 ? 'rgba(16,185,129,0.08)' : 'rgba(245,158,11,0.08)' }}>
                            <div style={{ fontSize: '14px', fontWeight: 900, color: c.effectivenessRate >= 85 ? '#059669' : '#d97706' }}>
                              {c.effectivenessRate}%
                            </div>
                            <div style={{ fontSize: '10px', fontWeight: 800, color: 'rgba(15,42,74,0.6)' }}>
                              Efectividad
                            </div>
                          </div>

                          {c.totalOvertimeHours > 0 && (
                            <div style={{ textAlign: 'center', padding: '6px 12px', borderRadius: '10px', background: 'rgba(139,92,246,0.08)' }}>
                              <div style={{ fontSize: '14px', fontWeight: 900, color: '#7c3aed' }}>
                                {c.totalOvertimeHours}h
                              </div>
                              <div style={{ fontSize: '10px', fontWeight: 800, color: 'rgba(15,42,74,0.6)' }}>
                                Extras (${c.totalOvertimeAmount.toLocaleString('es-CO')})
                              </div>
                            </div>
                          )}

                          {c.totalVacationDays > 0 && (
                            <div style={{ textAlign: 'center', padding: '6px 12px', borderRadius: '10px', background: 'rgba(2,132,199,0.08)' }}>
                              <div style={{ fontSize: '14px', fontWeight: 900, color: '#0284c7' }}>
                                {c.totalVacationDays}d
                              </div>
                              <div style={{ fontSize: '10px', fontWeight: 800, color: 'rgba(15,42,74,0.6)' }}>
                                Vacaciones
                              </div>
                            </div>
                          )}

                          <button
                            onClick={() => setSelectedPerfCollabId(selectedPerfCollabId === c.user.id ? null : c.user.id)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '8px',
                              border: '1px solid rgba(15,42,74,0.15)',
                              background: '#ffffff',
                              color: '#0f2a4a',
                              fontSize: '11px',
                              fontWeight: 800,
                              cursor: 'pointer'
                            }}
                          >
                            {selectedPerfCollabId === c.user.id ? 'Ocultar Detalle' : 'Ver Piezas'}
                          </button>
                        </div>
                      </div>

                      {/* MATRIZ HORIZONTAL DÍA A DÍA (1 AL 31) */}
                      <div style={{ overflowX: 'auto', paddingBottom: '4px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${daysInPerfMonth}, minmax(32px, 1fr))`, gap: '4px', minWidth: '700px' }}>
                          {Array.from({ length: daysInPerfMonth }, (_, i) => i + 1).map((day) => {
                            const dData = c.daysMap[day] || { units: 0, piecesCount: 0, hasOvertime: false, isVacation: false, details: [] };
                            const hasUnits = dData.units > 0;
                            const isHigh = dData.units >= 3;

                            return (
                              <div
                                key={day}
                                title={`Día ${day} de ${MONTHS[perfMonth]}: ${dData.units} unidades producidas. ${dData.hasOvertime ? '[Horas Extras]' : ''} ${dData.isVacation ? '[Vacaciones]' : ''}\n${dData.details.join('\n')}`}
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '6px 2px',
                                  borderRadius: '8px',
                                  background: hasUnits
                                    ? isHigh
                                      ? 'rgba(16,185,129,0.12)'
                                      : 'rgba(2,132,199,0.1)'
                                    : dData.isVacation
                                      ? 'rgba(245,158,11,0.12)'
                                      : 'rgba(15,42,74,0.02)',
                                  border: hasUnits
                                    ? isHigh
                                      ? '1px solid rgba(16,185,129,0.3)'
                                      : '1px solid rgba(2,132,199,0.25)'
                                    : '1px solid transparent',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease'
                                }}
                              >
                                <span style={{ fontSize: '9px', fontWeight: 800, color: 'rgba(15,42,74,0.5)' }}>
                                  {day}
                                </span>

                                <div style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  background: hasUnits
                                    ? isHigh
                                      ? '#059669'
                                      : '#0284c7'
                                    : dData.isVacation
                                      ? '#f59e0b'
                                      : 'transparent',
                                  color: '#ffffff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '10px',
                                  fontWeight: 900
                                }}>
                                  {hasUnits ? dData.units : dData.isVacation ? '🌴' : dData.hasOvertime ? '⚡' : <span style={{ color: 'rgba(15,42,74,0.2)' }}>·</span>}
                                </div>

                                {dData.hasOvertime && hasUnits && (
                                  <span style={{ fontSize: '9px', lineHeight: 1 }}>⚡</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* PIE DE TARJETA: CADENCIA & DIAS ACTIVOS */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: 'rgba(15,42,74,0.6)', borderTop: '1px solid rgba(15,42,74,0.06)', paddingTop: '10px' }}>
                        <div>
                          <strong>Días Activos:</strong> {c.activeDaysCount} de {daysInPerfMonth} días • <strong>Promedio:</strong> {c.dailyAvg} unidades/día activo
                        </div>
                        <div>
                          <strong>Día Pico:</strong> {c.peakDay.day > 0 ? `Día ${c.peakDay.day} (${c.peakDay.units} unidades completadas)` : 'Sin entregas'}
                        </div>
                      </div>

                      {/* DESPLEGABLE DE DETALLE DE PIEZAS DEL MES */}
                      {selectedPerfCollabId === c.user.id && (
                        <div style={{ background: 'rgba(15,42,74,0.03)', borderRadius: '12px', padding: '14px', marginTop: '6px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a', marginBottom: '8px' }}>
                            Piezas trabajadas en {MONTHS[perfMonth]} {perfYear} ({c.uPieces.length} piezas):
                          </div>
                          {c.uPieces.length === 0 ? (
                            <div style={{ fontSize: '11px', color: 'rgba(15,42,74,0.5)', fontStyle: 'italic' }}>
                              No hay piezas registradas para este colaborador en el mes seleccionado.
                            </div>
                          ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '8px' }}>
                              {c.uPieces.map((p: any) => (
                                <div key={p.id} style={{ background: '#ffffff', borderRadius: '8px', padding: '8px 10px', border: '1px solid rgba(15,42,74,0.1)', fontSize: '11px' }}>
                                  <div style={{ fontWeight: 800, color: '#0f2a4a' }}>{p.title || 'Pieza sin título'}</div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '10px', color: 'rgba(15,42,74,0.6)' }}>
                                    <span>{p.type || 'ESTATICA'}</span>
                                    <span style={{ fontWeight: 700, color: p.status === 'APROBADO' ? '#059669' : '#d97706' }}>{p.status}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                    </div>
                  ))}
              </div>
            </div>

          </div>
        )}


        {/* VISTA 4: MÓDULO DE COLABORADORES & MÉTRICAS REALES (ADMIN / CUENTAS) */}
        {mainView === 'COLLABORATORS' && isAdminOrCuentas && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
            
            {/* KPI SUMMARY CARDS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
              
              {/* Card 1: Total Colaboradores */}
              <div className="liquid-glass-card" style={{ padding: '20px 22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Total Colaboradores
                  </span>
                  <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(0,112,243,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0070f3' }}>
                    <IconUsers size={18} />
                  </div>
                </div>
                <div style={{ fontSize: '28px', fontWeight: 900, color: '#0f2a4a', margin: '10px 0 2px' }}>
                  <RouletteNumber value={collaborators.length} />
                </div>
                <span style={{ fontSize: '11px', color: '#0070f3', fontWeight: 700 }}>
                  Tripulación activa en ZOIK
                </span>
              </div>

              {/* Card 2: Contratos por vencer (30 días) */}
              <div className="liquid-glass-card" style={{ padding: '20px 22px', border: collaborators.filter(c => c.contractStatus === 'POR_VENCER').length > 0 ? '1.5px solid rgba(245, 158, 11, 0.5)' : undefined }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Contratos por Vencer
                  </span>
                  <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(245,158,11,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d97706' }}>
                    <IconAlertCircle size={18} />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', margin: '10px 0 2px' }}>
                  <div style={{ fontSize: '28px', fontWeight: 900, color: collaborators.filter(c => c.contractStatus === 'POR_VENCER').length > 0 ? '#d97706' : '#0f2a4a' }}>
                    <RouletteNumber value={collaborators.filter(c => c.contractStatus === 'POR_VENCER').length} />
                  </div>
                  {collaborators.filter(c => c.contractStatus === 'POR_VENCER').length > 0 && (
                    <span style={{ fontSize: '11px', fontWeight: 800, color: '#b45309', background: '#fef3c7', padding: '2px 8px', borderRadius: '6px' }}>
                      ⚠️ Aviso a Alejandra
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
                  <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
                    Límite &le; 30 días
                  </span>
                  <button
                    onClick={handleCheckAllReminders}
                    disabled={checkingReminders}
                    style={{
                      background: 'rgba(245,158,11,0.15)',
                      border: '1px solid rgba(245,158,11,0.3)',
                      borderRadius: '8px',
                      padding: '4px 10px',
                      fontSize: '10px',
                      fontWeight: 800,
                      color: '#b45309',
                      cursor: 'pointer',
                      fontFamily: '"Montserrat", sans-serif'
                    }}
                  >
                    {checkingReminders ? 'Comprobando...' : 'Revisar Alertas'}
                  </button>
                </div>
              </div>

              {/* Card 3: Cumpleaños del Mes */}
              <div className="liquid-glass-card" style={{ padding: '20px 22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Cumpleaños del Mes
                  </span>
                  <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(168,85,247,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9333ea' }}>
                    <IconSparkles size={18} />
                  </div>
                </div>
                <div style={{ fontSize: '28px', fontWeight: 900, color: '#0f2a4a', margin: '10px 0 2px' }}>
                  <RouletteNumber value={collaborators.filter(c => {
                    if (!c.birthDate) return false;
                    const bdate = new Date(c.birthDate);
                    return bdate.getUTCMonth() === new Date().getMonth();
                  }).length} />
                </div>
                <span style={{ fontSize: '11px', color: '#9333ea', fontWeight: 700 }}>
                  {collaborators.filter(c => c.isBirthdayToday).length > 0 ? '🎉 ¡Hoy celebra un tripulante!' : 'Celebraciones de este mes'}
                </span>
              </div>

              {/* Card 4: Efectividad Promedio */}
              <div className="liquid-glass-card" style={{ padding: '20px 22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Efectividad Promedio
                  </span>
                  <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(16,185,129,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669' }}>
                    <IconTrophy size={18} />
                  </div>
                </div>
                <div style={{ fontSize: '28px', fontWeight: 900, color: '#059669', margin: '10px 0 2px' }}>
                  {collaborators.length > 0
                    ? Math.round(collaborators.reduce((acc, c) => acc + (c.metrics?.approvalRate || 100), 0) / collaborators.length)
                    : 100}%
                </div>
                <span style={{ fontSize: '11px', color: '#059669', fontWeight: 700 }}>
                  Tasa global de aprobación
                </span>
              </div>

            </div>

            {/* CONTROLES DE BÚSQUEDA Y FILTRADO */}
            <div className="liquid-glass-panel" style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '260px' }}>
                <div style={{ position: 'relative', width: '100%', maxWidth: '380px' }}>
                  <input
                    type="text"
                    placeholder="Buscar por nombre, cargo o correo..."
                    value={collabSearch}
                    onChange={e => setCollabSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '9px 14px 9px 36px',
                      borderRadius: '12px',
                      border: '1.5px solid rgba(15,42,74,0.1)',
                      background: 'rgba(255,255,255,0.85)',
                      fontSize: '12px',
                      fontWeight: 600,
                      outline: 'none',
                      fontFamily: '"Montserrat", sans-serif'
                    }}
                  />
                  <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                    <IconSearch size={15} />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '6px', overflowX: 'auto' }}>
                  {['TODOS', 'ADMIN', 'CUENTAS', 'CONTENIDO', 'DISENO', 'ESTRATEGIA'].map(role => (
                    <button
                      key={role}
                      onClick={() => setCollabRoleFilter(role)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '10px',
                        border: 'none',
                        background: collabRoleFilter === role ? '#0f2a4a' : 'rgba(15,42,74,0.06)',
                        color: collabRoleFilter === role ? '#ffffff' : '#0f2a4a',
                        fontSize: '11px',
                        fontWeight: 800,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontFamily: '"Montserrat", sans-serif'
                      }}
                    >
                      {role === 'TODOS' ? 'Todos' : role === 'DISENO' ? 'Diseño' : role}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filtro por estado contractual */}
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', marginRight: '4px' }}>Contrato:</span>
                {[
                  { key: 'TODOS', label: 'Todos' },
                  { key: 'VIGENTE', label: 'Vigentes' },
                  { key: 'POR_VENCER', label: '⚠️ Por Vencer' },
                  { key: 'INDEFINIDO', label: 'Indefinidos' }
                ].map(item => (
                  <button
                    key={item.key}
                    onClick={() => setCollabContractFilter(item.key)}
                    style={{
                      padding: '5px 10px',
                      borderRadius: '8px',
                      border: 'none',
                      background: collabContractFilter === item.key ? '#0070f3' : 'rgba(0,112,243,0.08)',
                      color: collabContractFilter === item.key ? '#ffffff' : '#0070f3',
                      fontSize: '11px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      fontFamily: '"Montserrat", sans-serif'
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* LISTA / GRID DE COLABORADORES */}
            {loadingCollaborators ? (
              <div className="liquid-glass-panel" style={{ padding: '60px', textAlign: 'center', color: '#64748b', fontWeight: 700 }}>
                Cargando información integral de colaboradores...
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '20px' }}>
                {collaborators
                  .filter(c => {
                    const matchSearch = collabSearch === '' ||
                      `${c.firstName} ${c.lastName}`.toLowerCase().includes(collabSearch.toLowerCase()) ||
                      (c.email || '').toLowerCase().includes(collabSearch.toLowerCase()) ||
                      (c.position || '').toLowerCase().includes(collabSearch.toLowerCase());
                    const matchRole = collabRoleFilter === 'TODOS' || c.role === collabRoleFilter;
                    const matchContract = collabContractFilter === 'TODOS' || c.contractStatus === collabContractFilter;
                    return matchSearch && matchRole && matchContract;
                  })
                  .map(c => {
                    const birthDateObj = c.birthDate ? new Date(c.birthDate) : null;
                    const formattedBirth = birthDateObj ? birthDateObj.toLocaleDateString('es-CO', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    }) : null;

                    const formattedStart = c.contractStart ? new Date(c.contractStart).toLocaleDateString('es-CO', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    }) : 'No registrada';

                    const formattedEnd = c.contractEnd ? new Date(c.contractEnd).toLocaleDateString('es-CO', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    }) : null;

                    return (
                      <div key={c.id} className="liquid-glass-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        
                        {/* HEADER DEL COLABORADOR */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ position: 'relative' }}>
                              {c.avatar ? (
                                <img
                                  src={c.avatar}
                                  alt={c.firstName}
                                  style={{ width: '48px', height: '48px', borderRadius: '14px', objectFit: 'cover', border: '2px solid #ffffff', boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}
                                />
                              ) : (
                                <div style={{
                                  width: '48px',
                                  height: '48px',
                                  borderRadius: '14px',
                                  background: 'linear-gradient(135deg, #0070f3 0%, #004b99 100%)',
                                  color: '#ffffff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '17px',
                                  fontWeight: 900,
                                  boxShadow: '0 4px 10px rgba(0,112,243,0.3)'
                                }}>
                                  {(c.firstName || 'U')[0]}{(c.lastName || '')[0]}
                                </div>
                              )}
                              <span
                                className="pulse-online"
                                style={{
                                  position: 'absolute',
                                  bottom: '-2px',
                                  right: '-2px',
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  background: c.active ? '#10b981' : '#94a3b8',
                                  border: '2px solid #ffffff'
                                }}
                              />
                            </div>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 900, color: '#0f2a4a' }}>
                                  {c.firstName} {c.lastName}
                                </h3>
                                {c.isBirthdayToday && (
                                  <span style={{ fontSize: '12px', background: '#fdf4ff', border: '1px solid #f0abfc', borderRadius: '6px', padding: '1px 6px', color: '#a855f7', fontWeight: 800 }}>
                                    🎂 ¡Hoy cumple!
                                  </span>
                                )}
                              </div>
                              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#0070f3', fontWeight: 700 }}>
                                {c.position || 'Tripulante Creativo'}
                              </p>
                              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>
                                {c.email}
                              </span>
                            </div>
                          </div>

                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '8px',
                            fontSize: '10px',
                            fontWeight: 900,
                            letterSpacing: '0.4px',
                            background: c.role === 'ADMIN' ? 'rgba(0,112,243,0.12)' : c.role === 'DISENO' ? 'rgba(16,185,129,0.12)' : 'rgba(15,42,74,0.08)',
                            color: c.role === 'ADMIN' ? '#0070f3' : c.role === 'DISENO' ? '#047857' : '#0f2a4a'
                          }}>
                            {c.role}
                          </span>
                        </div>

                        {/* SECCIÓN 1: DATOS PERSONALES & EMERGENCIA */}
                        <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: '14px', padding: '12px 14px', border: '1px solid rgba(15,42,74,0.06)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <span style={{ fontSize: '10px', fontWeight: 900, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Datos Personales & Emergencia
                          </span>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px' }}>
                            <div>
                              <span style={{ color: '#64748b', display: 'block', fontWeight: 600 }}>🎂 Fecha Nacimiento:</span>
                              <span style={{ color: '#0f2a4a', fontWeight: 700 }}>{formattedBirth || 'No registrada'}</span>
                            </div>
                            <div>
                              <span style={{ color: '#64748b', display: 'block', fontWeight: 600 }}>📍 Residencia:</span>
                              <span style={{ color: '#0f2a4a', fontWeight: 700, wordBreak: 'break-word' }}>{c.address || 'No registrada'}</span>
                            </div>
                          </div>
                          <div style={{ borderTop: '1px dashed rgba(15,42,74,0.08)', paddingTop: '6px', fontSize: '11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <span style={{ color: '#64748b', fontWeight: 600 }}>🆘 Emergencia: </span>
                              <span style={{ color: '#0f2a4a', fontWeight: 700 }}>{c.emergencyContactName || 'No registrado'}</span>
                            </div>
                            {c.emergencyContactPhone && (
                              <a
                                href={`tel:${c.emergencyContactPhone}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  color: '#0070f3',
                                  textDecoration: 'none',
                                  fontWeight: 800,
                                  background: 'rgba(0,112,243,0.1)',
                                  padding: '2px 8px',
                                  borderRadius: '6px'
                                }}
                              >
                                <IconPhone size={11} /> {c.emergencyContactPhone}
                              </a>
                            )}
                          </div>
                        </div>

                        {/* SECCIÓN 2: DETALLES DE CONTRATACIÓN */}
                        <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: '14px', padding: '12px 14px', border: '1px solid rgba(15,42,74,0.06)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '10px', fontWeight: 900, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Detalles de Contrato
                            </span>
                            
                            {/* BADGE ESTADO CONTRATUAL */}
                            {c.contractStatus === 'INDEFINIDO' && (
                              <span style={{ background: 'rgba(0,112,243,0.12)', color: '#0070f3', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 800 }}>
                                Término Indefinido
                              </span>
                            )}
                            {c.contractStatus === 'VIGENTE' && (
                              <span style={{ background: 'rgba(16,185,129,0.14)', color: '#047857', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 800 }}>
                                Vigente ({c.daysRemaining} días)
                              </span>
                            )}
                            {c.contractStatus === 'POR_VENCER' && (
                              <span style={{ background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 800, border: '1px solid #fde68a' }}>
                                ⚠️ Vence en {c.daysRemaining} días
                              </span>
                            )}
                            {c.contractStatus === 'VENCIDO' && (
                              <span style={{ background: '#fee2e2', color: '#b91c1c', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 800 }}>
                                🚨 Vencido hace {Math.abs(c.daysRemaining || 0)} días
                              </span>
                            )}
                            {c.contractStatus === 'SIN_FECHA' && (
                              <span style={{ background: 'rgba(15,42,74,0.08)', color: '#64748b', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: 800 }}>
                                Sin fecha límite
                              </span>
                            )}
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '8px', fontSize: '11px' }}>
                            <div>
                              <span style={{ color: '#64748b', display: 'block', fontWeight: 600 }}>Tipo de Contrato:</span>
                              <span style={{ color: '#0f2a4a', fontWeight: 700 }}>{c.contractType || 'Sin registrar'}</span>
                            </div>
                            <div>
                              <span style={{ color: '#64748b', display: 'block', fontWeight: 600 }}>Periodo:</span>
                              <span style={{ color: '#0f2a4a', fontWeight: 700 }}>
                                {formattedStart} &rarr; {formattedEnd || 'Indefinido'}
                              </span>
                            </div>
                          </div>

                          {user?.role === 'ADMIN' && c.baseSalary && (
                            <div style={{ borderTop: '1px dashed rgba(15,42,74,0.08)', paddingTop: '6px', fontSize: '11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ color: '#64748b', fontWeight: 600 }}>Salario Base:</span>
                              <span style={{ color: '#047857', fontWeight: 900 }}>
                                ${c.baseSalary.toLocaleString('es-CO')} COP
                              </span>
                            </div>
                          )}

                          {c.contractStatus === 'POR_VENCER' && (
                            <div style={{ background: '#fffbeb', borderRadius: '8px', padding: '6px 10px', fontSize: '10px', color: '#92400e', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>Aviso 1 mes a Alejandra: {c.contractReminderSent ? '✅ Notificada' : '⏳ Pendiente'}</span>
                              <button
                                onClick={() => handleSendSingleReminder(c)}
                                disabled={sendingReminderId === c.id}
                                style={{
                                  background: '#f59e0b',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '3px 8px',
                                  fontSize: '10px',
                                  fontWeight: 800,
                                  cursor: 'pointer',
                                  fontFamily: '"Montserrat", sans-serif'
                                }}
                              >
                                {sendingReminderId === c.id ? 'Enviando...' : 'Reenviar Correo'}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* SECCIÓN 3: MÉTRICAS REALES DE RENDIMIENTO */}
                        <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: '14px', padding: '12px 14px', border: '1px solid rgba(15,42,74,0.06)' }}>
                          <span style={{ fontSize: '10px', fontWeight: 900, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>
                            Métricas Reales de Rendimiento
                          </span>
                          
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', textAlign: 'center' }}>
                            <div style={{ background: 'rgba(15,42,74,0.04)', borderRadius: '8px', padding: '8px 4px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 900, color: '#0f2a4a', display: 'block' }}>
                                {c.metrics?.totalPieces || 0}
                              </span>
                              <span style={{ fontSize: '9px', fontWeight: 700, color: '#64748b' }}>Total Piezas</span>
                            </div>
                            <div style={{ background: 'rgba(0,112,243,0.06)', borderRadius: '8px', padding: '8px 4px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 900, color: '#0070f3', display: 'block' }}>
                                {c.metrics?.monthPieces || 0}
                              </span>
                              <span style={{ fontSize: '9px', fontWeight: 700, color: '#0070f3' }}>Este Mes</span>
                            </div>
                            <div style={{ background: 'rgba(16,185,129,0.08)', borderRadius: '8px', padding: '8px 4px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 900, color: '#059669', display: 'block' }}>
                                {c.metrics?.totalApproved || 0}
                              </span>
                              <span style={{ fontSize: '9px', fontWeight: 700, color: '#059669' }}>Aprobadas</span>
                            </div>
                            <div style={{ background: 'rgba(124,58,237,0.06)', borderRadius: '8px', padding: '8px 4px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 900, color: '#7c3aed', display: 'block' }}>
                                {c.metrics?.activeBrandsCount || 0}
                              </span>
                              <span style={{ fontSize: '9px', fontWeight: 700, color: '#7c3aed' }}>Marcas</span>
                            </div>
                          </div>

                          {/* BARRA DE APROBACIÓN */}
                          <div style={{ marginTop: '10px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', fontWeight: 800, marginBottom: '3px' }}>
                              <span style={{ color: '#64748b' }}>Efectividad de entrega</span>
                              <span style={{ color: (c.metrics?.approvalRate || 100) >= 80 ? '#059669' : '#d97706' }}>
                                {c.metrics?.approvalRate || 100}%
                              </span>
                            </div>
                            <div style={{ height: '6px', background: 'rgba(15,42,74,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                              <div
                                style={{
                                  height: '100%',
                                  width: `${c.metrics?.approvalRate || 100}%`,
                                  background: (c.metrics?.approvalRate || 100) >= 80 ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)' : 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)',
                                  borderRadius: '3px',
                                  transition: 'width 0.5s ease'
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* ACCIONES DEL COLABORADOR */}
                        <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', paddingTop: '4px' }}>
                          <button
                            type="button"
                            onClick={() => navigate(`/permissions?userId=${c.id}`)}
                            style={{
                              padding: '8px 12px',
                              borderRadius: '10px',
                              border: '1px solid rgba(0, 112, 243, 0.3)',
                              background: 'rgba(0, 112, 243, 0.08)',
                              color: '#0070f3',
                              fontSize: '11px',
                              fontWeight: 800,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              fontFamily: '"Montserrat", sans-serif'
                            }}
                          >
                            <IconCalendarDays size={13} color="#0070f3" />
                            <span>Permisos & Horas Extra</span>
                          </button>
                          <button
                            onClick={() => handleOpenEditCollab(c)}
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              borderRadius: '10px',
                              border: '1px solid rgba(0,112,243,0.3)',
                              background: 'rgba(0,112,243,0.08)',
                              color: '#0070f3',
                              fontSize: '11px',
                              fontWeight: 800,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              fontFamily: '"Montserrat", sans-serif',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <IconEdit size={13} /> Editar RRHH
                          </button>

                          <button
                            onClick={() => { setSelectedUserId(c.id); setMainView('CALENDAR'); }}
                            style={{
                              padding: '8px 14px',
                              borderRadius: '10px',
                              border: '1px solid rgba(15,42,74,0.12)',
                              background: 'rgba(255,255,255,0.8)',
                              color: '#0f2a4a',
                              fontSize: '11px',
                              fontWeight: 800,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontFamily: '"Montserrat", sans-serif'
                            }}
                          >
                            <IconEye size={13} color="#0070f3" /> Agenda
                          </button>
                        </div>

                      </div>
                    );
                  })}
              </div>
            )}

          </div>
        )}


        {/* VISTA 5: MÓDULO DE HORAS EXTRAS & LIQUIDACIÓN (NICOLÁS, ALEJANDRA & EQUIPO) */}
        {mainView === 'OVERTIME' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
            
            {/* KPI BANNERS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
              
              {/* Card 1: Total Liquidado Mes */}
              <div className="liquid-glass-card" style={{ padding: '20px 22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Total Liquidado (Este Mes)
                  </span>
                  <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(16,185,129,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669' }}>
                    <IconTrophy size={18} />
                  </div>
                </div>
                <div style={{ fontSize: '26px', fontWeight: 900, color: '#047857', margin: '10px 0 2px' }}>
                  ${(overtimeStats.totalAmountThisMonth || 0).toLocaleString('es-CO')} <span style={{ fontSize: '14px', fontWeight: 700 }}>COP</span>
                </div>
                <span style={{ fontSize: '11px', color: '#059669', fontWeight: 700 }}>
                  Aprobado y listo para nómina contable
                </span>
              </div>

              {/* Card 2: Horas Aprobadas */}
              <div className="liquid-glass-card" style={{ padding: '20px 22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Horas Aprobadas
                  </span>
                  <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(0,112,243,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0070f3' }}>
                    <IconClock size={18} />
                  </div>
                </div>
                <div style={{ fontSize: '28px', fontWeight: 900, color: '#0f2a4a', margin: '10px 0 2px' }}>
                  <RouletteNumber value={overtimeStats.totalHoursThisMonth || 0} /> <span style={{ fontSize: '14px', fontWeight: 700 }}>horas</span>
                </div>
                <span style={{ fontSize: '11px', color: '#0070f3', fontWeight: 700 }}>
                  En {overtimeStats.approvedThisMonthCount} jornada(s) del mes
                </span>
              </div>

              {/* Card 3: Solicitudes Pendientes para Nicolás */}
              <div className="liquid-glass-card" style={{ padding: '20px 22px', border: overtimeStats.pendingCount > 0 ? '1.5px solid rgba(245, 158, 11, 0.6)' : undefined }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Pendientes de Aprobación
                  </span>
                  <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(245,158,11,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d97706' }}>
                    <IconAlertCircle size={18} />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', margin: '10px 0 2px' }}>
                  <div style={{ fontSize: '28px', fontWeight: 900, color: overtimeStats.pendingCount > 0 ? '#d97706' : '#0f2a4a' }}>
                    <RouletteNumber value={overtimeStats.pendingCount || 0} />
                  </div>
                  {overtimeStats.pendingCount > 0 && (
                    <span style={{ fontSize: '11px', fontWeight: 800, color: '#b45309', background: '#fef3c7', padding: '2px 8px', borderRadius: '6px' }}>
                      Revisión de Nicolás
                    </span>
                  )}
                </div>
                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
                  {overtimeStats.pendingCount === 0 ? 'Al día, sin solicitudes pendientes' : 'Requieren aprobación digital'}
                </span>
              </div>

              {/* Card 4: Colaboradores */}
              <div className="liquid-glass-card" style={{ padding: '20px 22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Colaboradores en Nómina Extra
                  </span>
                  <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(124,58,237,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7c3aed' }}>
                    <IconUsers size={18} />
                  </div>
                </div>
                <div style={{ fontSize: '28px', fontWeight: 900, color: '#0f2a4a', margin: '10px 0 2px' }}>
                  <RouletteNumber value={overtimeStats.collaboratorsCount || 0} />
                </div>
                <span style={{ fontSize: '11px', color: '#7c3aed', fontWeight: 700 }}>
                  Tripulantes liquidados este mes
                </span>
              </div>

            </div>

            {/* SECCIÓN ESPECIAL DE APROBACIÓN PARA NICOLÁS FERNÁNDEZ (SI HAY PENDIENTES) */}
            {(isNicolas || isAdminOrCuentas) && overtimeRecords.filter(r => r.status === 'PENDIENTE').length > 0 && (
              <div className="liquid-glass-panel" style={{ padding: '22px 26px', border: '1.5px solid rgba(245, 158, 11, 0.4)', background: 'rgba(254, 243, 199, 0.35)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 900, color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IconAlertCircle size={18} color="#d97706" />
                      <span>Solicitudes Pendientes de Aprobación por Nicolás Fernández</span>
                    </h3>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#78350f', fontWeight: 600 }}>
                      Al dar clic en Aprobar, el sistema calculará automáticamente las tarifas por franja horaria colombiana y archivará el PDF membretado en el perfil de Alejandra Fernández.
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {overtimeRecords.filter(r => r.status === 'PENDIENTE').map(r => (
                    <div key={r.id} style={{
                      background: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '14px',
                      padding: '16px 20px',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '14px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div style={{
                          width: '42px',
                          height: '42px',
                          borderRadius: '12px',
                          background: 'linear-gradient(135deg, #0070f3 0%, #005bb5 100%)',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '15px',
                          fontWeight: 900
                        }}>
                          {(r.user?.firstName || 'U')[0]}
                        </div>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 900, color: '#0f2a4a' }}>
                            {r.user?.firstName} {r.user?.lastName}
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
                            {r.user?.position || 'Colaborador'} · {new Date(r.date).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                          <div style={{ fontSize: '12px', color: '#0070f3', fontWeight: 700, marginTop: '2px' }}>
                            {r.startTime} a {r.endTime} ({r.hours} horas) &mdash; <span style={{ color: '#475569', fontWeight: 600 }}>"{r.reason}"</span>
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button
                          onClick={() => handleReviewOvertimeDecision(r.id, 'APROBADO')}
                          disabled={reviewingOvertimeId === r.id}
                          style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            border: 'none',
                            borderRadius: '10px',
                            padding: '9px 18px',
                            color: '#ffffff',
                            fontSize: '12px',
                            fontWeight: 800,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                            fontFamily: '"Montserrat", sans-serif'
                          }}
                        >
                          <IconCheck size={14} color="#ffffff" />
                          {reviewingOvertimeId === r.id ? 'Calculando...' : 'Aprobar & Liquidar'}
                        </button>

                        <button
                          onClick={() => {
                            const notes = prompt('Motivo u observación del rechazo:');
                            if (notes !== null) {
                              handleReviewOvertimeDecision(r.id, 'RECHAZADO', notes);
                            }
                          }}
                          disabled={reviewingOvertimeId === r.id}
                          style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '10px',
                            padding: '9px 14px',
                            color: '#dc2626',
                            fontSize: '12px',
                            fontWeight: 800,
                            cursor: 'pointer',
                            fontFamily: '"Montserrat", sans-serif'
                          }}
                        >
                          <IconX size={14} color="#dc2626" /> Rechazar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TABLA HISTORIAL CONSOLIDADO (PARA ALEJANDRA FERNÁNDEZ & ADMINS) */}
            <div className="liquid-glass-panel" style={{ padding: '24px 28px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '14px' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#0f2a4a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <IconClock size={18} color="#0070f3" />
                    <span>Registro Consolidado de Horas Extras & Comprobantes PDF</span>
                  </h2>
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(15,42,74,0.6)', fontWeight: 600 }}>
                    Comprobantes oficiales membretados para la gestión contable de Alejandra Fernández.
                  </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  {/* Buscador */}
                  <div style={{ position: 'relative', width: '240px' }}>
                    <input
                      type="text"
                      placeholder="Buscar por colaborador o motivo..."
                      value={overtimeSearch}
                      onChange={e => setOvertimeSearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px 8px 32px',
                        borderRadius: '10px',
                        border: '1.5px solid rgba(15,42,74,0.1)',
                        background: 'rgba(255,255,255,0.85)',
                        fontSize: '11px',
                        fontWeight: 600,
                        outline: 'none',
                        fontFamily: '"Montserrat", sans-serif',
                        boxSizing: 'border-box'
                      }}
                    />
                    <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                      <IconSearch size={13} />
                    </div>
                  </div>

                  {/* Filtro estado */}
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {['TODOS', 'APROBADO', 'PENDIENTE', 'RECHAZADO'].map(st => (
                      <button
                        key={st}
                        onClick={() => setOvertimeStatusFilter(st)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '8px',
                          border: 'none',
                          background: overtimeStatusFilter === st ? '#0f2a4a' : 'rgba(15,42,74,0.06)',
                          color: overtimeStatusFilter === st ? '#ffffff' : '#0f2a4a',
                          fontSize: '11px',
                          fontWeight: 800,
                          cursor: 'pointer',
                          fontFamily: '"Montserrat", sans-serif'
                        }}
                      >
                        {st === 'TODOS' ? 'Todos' : st === 'APROBADO' ? 'Aprobadas' : st === 'PENDIENTE' ? 'Pendientes' : 'Rechazadas'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {loadingOvertime ? (
                <div style={{ padding: '50px', textAlign: 'center', color: '#64748b', fontWeight: 700 }}>
                  Cargando liquidaciones y horas extras...
                </div>
              ) : overtimeRecords.length === 0 ? (
                <div style={{ padding: '50px', textAlign: 'center', color: '#64748b', fontWeight: 700 }}>
                  No hay registros de horas extras en el periodo seleccionado.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid rgba(15,42,74,0.08)', background: 'rgba(15,42,74,0.04)' }}>
                        <th style={{ padding: '12px 14px', fontWeight: 800, color: '#0f2a4a' }}>Fecha</th>
                        <th style={{ padding: '12px 14px', fontWeight: 800, color: '#0f2a4a' }}>Colaborador</th>
                        <th style={{ padding: '12px 14px', fontWeight: 800, color: '#0f2a4a' }}>Horario</th>
                        <th style={{ padding: '12px 14px', fontWeight: 800, color: '#0f2a4a' }}>Motivo / Marca</th>
                        <th style={{ padding: '12px 14px', fontWeight: 800, color: '#0f2a4a', textAlign: 'center' }}>Desglose</th>
                        <th style={{ padding: '12px 14px', fontWeight: 800, color: '#0f2a4a', textAlign: 'center' }}>Estado</th>
                        <th style={{ padding: '12px 14px', fontWeight: 800, color: '#0f2a4a', textAlign: 'right' }}>Total COP</th>
                        <th style={{ padding: '12px 14px', fontWeight: 800, color: '#0f2a4a', textAlign: 'right' }}>Comprobante</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overtimeRecords
                        .filter(r => {
                          const matchSearch = overtimeSearch === '' ||
                            `${r.user?.firstName} ${r.user?.lastName}`.toLowerCase().includes(overtimeSearch.toLowerCase()) ||
                            (r.reason || '').toLowerCase().includes(overtimeSearch.toLowerCase());
                          const matchStatus = overtimeStatusFilter === 'TODOS' || r.status === overtimeStatusFilter;
                          return matchSearch && matchStatus;
                        })
                        .map(r => (
                          <tr key={r.id} style={{ borderBottom: '1px solid rgba(15,42,74,0.06)' }}>
                            <td style={{ padding: '14px', fontWeight: 700, color: '#0f2a4a', whiteSpace: 'nowrap' }}>
                              {new Date(r.date).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </td>
                            <td style={{ padding: '14px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                  width: '30px',
                                  height: '30px',
                                  borderRadius: '8px',
                                  background: 'linear-gradient(135deg, #0070f3 0%, #005bb5 100%)',
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '11px',
                                  fontWeight: 800
                                }}>
                                  {(r.user?.firstName || 'U')[0]}
                                </div>
                                <div>
                                  <div style={{ fontWeight: 800, color: '#0f2a4a' }}>
                                    {r.user?.firstName} {r.user?.lastName}
                                  </div>
                                  <div style={{ fontSize: '10px', color: '#64748b' }}>
                                    {r.user?.position || 'Colaborador'}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '14px', color: '#0f2a4a', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {r.startTime} &rarr; {r.endTime} ({r.hours}h)
                            </td>
                            <td style={{ padding: '14px', color: '#475569', fontWeight: 600, maxWidth: '220px' }}>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.reason}>
                                {r.reason}
                              </div>
                            </td>
                            <td style={{ padding: '14px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap', fontSize: '10px' }}>
                                {r.diurnaHours > 0 && <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: '4px', fontWeight: 800 }}>D: {r.diurnaHours}h</span>}
                                {r.nocturnaHours > 0 && <span style={{ background: '#f3e8ff', color: '#7e22ce', padding: '2px 6px', borderRadius: '4px', fontWeight: 800 }}>N: {r.nocturnaHours}h</span>}
                                {r.festivaDiurnaHours > 0 && <span style={{ background: '#fef3c7', color: '#b45309', padding: '2px 6px', borderRadius: '4px', fontWeight: 800 }}>FD: {r.festivaDiurnaHours}h</span>}
                                {r.festivaNocturnaHours > 0 && <span style={{ background: '#fee2e2', color: '#b91c1c', padding: '2px 6px', borderRadius: '4px', fontWeight: 800 }}>FN: {r.festivaNocturnaHours}h</span>}
                              </div>
                            </td>
                            <td style={{ padding: '14px', textAlign: 'center' }}>
                              <span style={{
                                padding: '3px 8px',
                                borderRadius: '6px',
                                fontSize: '10px',
                                fontWeight: 800,
                                background: r.status === 'APROBADO' ? '#d1fae5' : r.status === 'PENDIENTE' ? '#fef3c7' : '#fee2e2',
                                color: r.status === 'APROBADO' ? '#065f46' : r.status === 'PENDIENTE' ? '#b45309' : '#b91c1c'
                              }}>
                                {r.status === 'APROBADO' ? '✓ Aprobado' : r.status === 'PENDIENTE' ? '⏳ Pendiente' : '✕ Rechazado'}
                              </span>
                            </td>
                            <td style={{ padding: '14px', textAlign: 'right', fontWeight: 900, color: r.status === 'APROBADO' ? '#047857' : '#64748b' }}>
                              {r.totalAmount > 0 ? `$${Math.round(r.totalAmount).toLocaleString('es-CO')} COP` : '---'}
                            </td>
                            <td style={{ padding: '14px', textAlign: 'right' }}>
                              {r.status === 'APROBADO' ? (
                                <button
                                  onClick={() => handleDownloadOvertimePDF(r.id, `${r.user?.firstName} ${r.user?.lastName}`)}
                                  className="liquid-glass-item"
                                  style={{
                                    padding: '6px 12px',
                                    fontSize: '11px',
                                    fontWeight: 800,
                                    color: '#0070f3',
                                    cursor: 'pointer',
                                    fontFamily: '"Montserrat", sans-serif',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                  title="Descargar comprobante oficial membretado"
                                >
                                  <IconDownload size={12} color="#0070f3" /> PDF Oficial
                                </button>
                              ) : r.status === 'PENDIENTE' && (isNicolas || isAdminOrCuentas) ? (
                                <button
                                  onClick={() => handleReviewOvertimeDecision(r.id, 'APROBADO')}
                                  style={{
                                    padding: '5px 10px',
                                    fontSize: '11px',
                                    fontWeight: 800,
                                    color: '#ffffff',
                                    background: '#10b981',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontFamily: '"Montserrat", sans-serif'
                                  }}
                                >
                                  Aprobar
                                </button>
                              ) : (
                                <span style={{ fontSize: '11px', color: '#94a3b8' }}>N/A</span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        )}


        {/* VISTA 6: MÓDULO DE SOLICITUDES Y CÁLCULO DE VACACIONES (ALEJANDRA & EQUIPO) */}
        {mainView === 'VACATIONS' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
            
            {/* KPI BANNERS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
              
              {/* Card 1: Días Aprobados Año */}
              <div className="liquid-glass-card" style={{ padding: '20px 22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Días Aprobados (Año 2026)
                  </span>
                  <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(2,132,199,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0284c7' }}>
                    <IconCalendar size={18} />
                  </div>
                </div>
                <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f2a4a', marginTop: '10px' }}>
                  {vacationStats.totalDaysThisYear} días
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(15,42,74,0.5)', marginTop: '4px' }}>
                  {vacationStats.approvedThisYearCount} solicitudes aprobadas en el año
                </div>
              </div>

              {/* Card 2: Total Liquidado */}
              <div className="liquid-glass-card" style={{ padding: '20px 22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Total Liquidado en Vacaciones
                  </span>
                  <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(16,185,129,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669' }}>
                    <IconTrophy size={18} />
                  </div>
                </div>
                <div style={{ fontSize: '26px', fontWeight: 900, color: '#059669', marginTop: '10px' }}>
                  ${(vacationStats.totalAmountThisYear || 0).toLocaleString('es-CO')}
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(15,42,74,0.5)', marginTop: '4px' }}>
                  Liquidación conforme a CST Art. 186-192
                </div>
              </div>

              {/* Card 3: Solicitudes Pendientes */}
              <div className="liquid-glass-card" style={{ padding: '20px 22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Pendientes de Aprobación
                  </span>
                  <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: vacationStats.pendingCount > 0 ? 'rgba(236,72,153,0.15)' : 'rgba(15,42,74,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: vacationStats.pendingCount > 0 ? '#db2777' : '#0f2a4a' }}>
                    <IconClock size={18} />
                  </div>
                </div>
                <div style={{ fontSize: '26px', fontWeight: 900, color: vacationStats.pendingCount > 0 ? '#db2777' : '#0f2a4a', marginTop: '10px' }}>
                  {vacationStats.pendingCount}
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(15,42,74,0.5)', marginTop: '4px' }}>
                  {isAlejandra ? 'Requieren tu aprobación contable' : 'En cola de revisión oficial'}
                </div>
              </div>

              {/* Card 4: Colaboradores con Vacaciones */}
              <div className="liquid-glass-card" style={{ padding: '20px 22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Colaboradores Beneficiarios
                  </span>
                  <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(99,102,241,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1' }}>
                    <IconUsers size={18} />
                  </div>
                </div>
                <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f2a4a', marginTop: '10px' }}>
                  {vacationStats.collaboratorsCount}
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(15,42,74,0.5)', marginTop: '4px' }}>
                  Tripulantes con descanso programado en 2026
                </div>
              </div>

            </div>

            {/* BANDEJA DE APROBACIÓN PARA ALEJANDRA FERNÁNDEZ & ADMIN */}
            {(isAlejandra || isAdminOrCuentas) && vacationRecords.filter(r => r.status === 'PENDIENTE').length > 0 && (
              <div className="liquid-glass-card" style={{ padding: '22px 24px', borderLeft: '4px solid #db2777' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }}>📋</span>
                    <div>
                      <h3 style={{ fontSize: '15px', fontWeight: 900, color: '#0f2a4a', margin: 0 }}>
                        Bandeja de Aprobación de Vacaciones — Alejandra Fernández
                      </h3>
                      <p style={{ fontSize: '12px', color: 'rgba(15,42,74,0.6)', margin: '2px 0 0 0' }}>
                        Revisa las solicitudes radicadas, valida el cálculo CST y emite el documento oficial firmado para nómina.
                      </p>
                    </div>
                  </div>
                  <span style={{
                    background: '#db2777',
                    color: '#fff',
                    borderRadius: '20px',
                    padding: '4px 12px',
                    fontSize: '11px',
                    fontWeight: 900
                  }}>
                    {vacationRecords.filter(r => r.status === 'PENDIENTE').length} pendientes
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '16px' }}>
                  {vacationRecords
                    .filter(r => r.status === 'PENDIENTE')
                    .map((req) => (
                      <div
                        key={req.id}
                        style={{
                          background: 'rgba(255,255,255,0.7)',
                          backdropFilter: 'blur(8px)',
                          borderRadius: '16px',
                          border: '1px solid rgba(219,39,119,0.2)',
                          padding: '18px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px'
                        }}
                      >
                        {/* Cabecera Solicitud */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '38px',
                              height: '38px',
                              borderRadius: '10px',
                              background: '#0284c7',
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 900,
                              fontSize: '14px'
                            }}>
                              {req.user.firstName.charAt(0)}{req.user.lastName.charAt(0)}
                            </div>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f2a4a' }}>
                                {req.user.firstName} {req.user.lastName}
                              </div>
                              <div style={{ fontSize: '11px', color: 'rgba(15,42,74,0.6)' }}>
                                {req.user.position || 'Tripulante ZOIK'} • {req.user.contractType || 'Contrato Laboral'}
                              </div>
                            </div>
                          </div>
                          <span style={{
                            fontSize: '10px',
                            fontWeight: 800,
                            padding: '3px 8px',
                            borderRadius: '8px',
                            background: 'rgba(2,132,199,0.1)',
                            color: '#0284c7',
                            border: '1px solid rgba(2,132,199,0.2)'
                          }}>
                            ZOIK-VAC-{req.id.substring(0, 6).toUpperCase()}
                          </span>
                        </div>

                        {/* Detalles de la Solicitud */}
                        <div style={{ background: 'rgba(15,42,74,0.03)', borderRadius: '10px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ fontSize: '11px', color: 'rgba(15,42,74,0.7)' }}>
                            <strong style={{ color: '#0f2a4a' }}>A quién:</strong> {req.recipientName || 'Alejandra Fernández'}
                          </div>
                          <div style={{ fontSize: '11px', color: 'rgba(15,42,74,0.7)' }}>
                            <strong style={{ color: '#0f2a4a' }}>Motivo:</strong> {req.reason}
                          </div>
                          <div style={{ fontSize: '11px', color: 'rgba(15,42,74,0.7)' }}>
                            <strong style={{ color: '#0f2a4a' }}>Periodo:</strong> Del {new Date(req.startDate).toLocaleDateString('es-CO')} al {new Date(req.endDate).toLocaleDateString('es-CO')} ({req.daysRequested} días hábiles)
                          </div>
                          {req.details && (
                            <div style={{ fontSize: '11px', color: 'rgba(15,42,74,0.8)', fontStyle: 'italic', marginTop: '4px', borderLeft: '2px solid #0284c7', paddingLeft: '8px' }}>
                              "{req.details}"
                            </div>
                          )}
                        </div>

                        {/* Liquidación CST */}
                        <div style={{
                          background: 'rgba(16,185,129,0.06)',
                          borderRadius: '10px',
                          padding: '10px 12px',
                          border: '1px dashed rgba(16,185,129,0.3)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <div style={{ fontSize: '10px', fontWeight: 800, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase' }}>
                              Liquidación Estimada (CST)
                            </div>
                            <div style={{ fontSize: '11px', color: '#0f2a4a', marginTop: '2px' }}>
                              Base ${req.salaryBase ? `${req.salaryBase.toLocaleString('es-CO')}` : 'Sin registrar'} / 30 × {req.daysRequested} d
                            </div>
                          </div>
                          <div style={{ fontSize: '15px', fontWeight: 900, color: '#059669' }}>
                            ${(req.calculatedValue || 0).toLocaleString('es-CO')}
                          </div>
                        </div>

                        {/* Botones de Acción */}
                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                          <button
                            disabled={reviewingVacationId === req.id}
                            onClick={() => {
                              const note = window.prompt('Notas u observaciones de aprobación para nómina:', 'Aprobadas conforme a programación de nómina y CST Art. 186');
                              if (note !== null) {
                                handleReviewVacation(req.id, 'APROBADO', note);
                              }
                            }}
                            style={{
                              flex: 1,
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              border: 'none',
                              borderRadius: '10px',
                              padding: '8px 12px',
                              color: '#ffffff',
                              fontSize: '12px',
                              fontWeight: 800,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            <IconCheck size={14} /> Aprobar
                          </button>

                          <button
                            disabled={reviewingVacationId === req.id}
                            onClick={() => {
                              const note = window.prompt('Motivo de rechazo o aplazamiento de vacaciones:', 'Reprogramación por requerimientos de entregables');
                              if (note) {
                                handleReviewVacation(req.id, 'RECHAZADO', note);
                              }
                            }}
                            style={{
                              flex: 1,
                              background: 'rgba(239,68,68,0.1)',
                              border: '1px solid rgba(239,68,68,0.2)',
                              borderRadius: '10px',
                              padding: '8px 12px',
                              color: '#dc2626',
                              fontSize: '12px',
                              fontWeight: 800,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px'
                            }}
                          >
                            <IconX size={14} /> Rechazar
                          </button>

                          <button
                            onClick={() => handleDownloadVacationPDF(req.id, req.user.firstName)}
                            title="Ver Comprobante PDF Oficial"
                            style={{
                              width: '36px',
                              borderRadius: '10px',
                              border: '1px solid rgba(15,42,74,0.15)',
                              background: '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#0f2a4a',
                              cursor: 'pointer'
                            }}
                          >
                            <IconDownload size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* TABLA HISTORIAL Y MATRIZ GENERAL DE VACACIONES */}
            <div className="liquid-glass-card" style={{ padding: '22px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px', marginBottom: '20px' }}>
                <div>
                  <h3 style={{ fontSize: '15px', fontWeight: 900, color: '#0f2a4a', margin: 0 }}>
                    Registro Consolidado de Solicitudes & Comprobantes de Vacaciones
                  </h3>
                  <p style={{ fontSize: '12px', color: 'rgba(15,42,74,0.6)', margin: '3px 0 0 0' }}>
                    Historial auditado de radicados, aprobaciones y descarga de comprobantes oficiales con membrete y logo ZOIK
                  </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  {/* Buscador */}
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="Buscar colaborador, motivo..."
                      value={vacationSearch}
                      onChange={(e) => setVacationSearch(e.target.value)}
                      style={{
                        padding: '7px 12px 7px 30px',
                        borderRadius: '10px',
                        border: '1px solid rgba(15,42,74,0.15)',
                        background: 'rgba(255,255,255,0.8)',
                        fontSize: '12px',
                        outline: 'none',
                        fontFamily: '"Montserrat", sans-serif',
                        width: '200px'
                      }}
                    />
                    <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(15,42,74,0.4)', pointerEvents: 'none' }}>
                      <IconSearch size={14} />
                    </div>
                  </div>

                  {/* Filtro Estado */}
                  <div style={{ display: 'flex', background: 'rgba(15,42,74,0.06)', borderRadius: '10px', padding: '3px' }}>
                    {['TODAS', 'PENDIENTE', 'APROBADO', 'RECHAZADO'].map((st) => (
                      <button
                        key={st}
                        onClick={() => setVacationStatusFilter(st)}
                        style={{
                          padding: '5px 10px',
                          borderRadius: '8px',
                          border: 'none',
                          background: vacationStatusFilter === st ? '#ffffff' : 'transparent',
                          color: vacationStatusFilter === st ? '#0f2a4a' : 'rgba(15,42,74,0.6)',
                          fontSize: '11px',
                          fontWeight: 800,
                          cursor: 'pointer',
                          boxShadow: vacationStatusFilter === st ? '0 2px 6px rgba(0,0,0,0.06)' : 'none'
                        }}
                      >
                        {st}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => {
                      const today = new Date().toISOString().split('T')[0];
                      const end = new Date(Date.now() + 15 * 86400000).toISOString().split('T')[0];
                      setRequestVacationForm(prev => ({
                        ...prev,
                        startDate: today,
                        endDate: end,
                        daysRequested: 15
                      }));
                      setShowRequestVacationModal(true);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '7px 14px',
                      color: '#ffffff',
                      fontSize: '12px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span>+ Radicar Solicitud</span>
                  </button>
                </div>
              </div>

              {/* TABLA */}
              {loadingVacations ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(15,42,74,0.5)', fontSize: '13px' }}>
                  Cargando solicitudes de vacaciones...
                </div>
              ) : vacationRecords.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(15,42,74,0.5)', fontSize: '13px' }}>
                  No hay solicitudes de vacaciones registradas en el sistema.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(15,42,74,0.08)', color: 'rgba(15,42,74,0.5)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <th style={{ padding: '12px 14px', fontWeight: 800 }}>Folio Radicado</th>
                        <th style={{ padding: '12px 14px', fontWeight: 800 }}>Colaborador</th>
                        <th style={{ padding: '12px 14px', fontWeight: 800 }}>Destinatario</th>
                        <th style={{ padding: '12px 14px', fontWeight: 800 }}>Motivo & Detalle</th>
                        <th style={{ padding: '12px 14px', fontWeight: 800 }}>Periodo & Días</th>
                        <th style={{ padding: '12px 14px', fontWeight: 800 }}>Liquidación CST</th>
                        <th style={{ padding: '12px 14px', fontWeight: 800 }}>Estado</th>
                        <th style={{ padding: '12px 14px', fontWeight: 800, textAlign: 'right' }}>Comprobante</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vacationRecords
                        .filter(r => vacationStatusFilter === 'TODAS' || r.status === vacationStatusFilter)
                        .filter(r => {
                          if (!vacationSearch) return true;
                          const s = vacationSearch.toLowerCase();
                          return (
                            (r.user?.firstName || '').toLowerCase().includes(s) ||
                            (r.user?.lastName || '').toLowerCase().includes(s) ||
                            (r.reason || '').toLowerCase().includes(s) ||
                            (r.details || '').toLowerCase().includes(s) ||
                            (r.recipientName || '').toLowerCase().includes(s)
                          );
                        })
                        .map((req) => (
                          <tr key={req.id} style={{ borderBottom: '1px solid rgba(15,42,74,0.05)', transition: 'background 0.2s ease' }}>
                            {/* Folio */}
                            <td style={{ padding: '12px 14px', fontWeight: 800, color: '#0284c7' }}>
                              ZOIK-VAC-{req.id.substring(0, 6).toUpperCase()}
                            </td>

                            {/* Colaborador */}
                            <td style={{ padding: '12px 14px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                  width: '28px',
                                  height: '28px',
                                  borderRadius: '8px',
                                  background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 800,
                                  fontSize: '11px'
                                }}>
                                  {req.user.firstName.charAt(0)}{req.user.lastName.charAt(0)}
                                </div>
                                <div>
                                  <div style={{ fontWeight: 800, color: '#0f2a4a' }}>
                                    {req.user.firstName} {req.user.lastName}
                                  </div>
                                  <div style={{ fontSize: '10px', color: 'rgba(15,42,74,0.5)' }}>
                                    {req.user.position || req.user.role}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Destinatario */}
                            <td style={{ padding: '12px 14px', color: '#0f2a4a', fontSize: '11px' }}>
                              {req.recipientName || 'Alejandra Fernández'}
                            </td>

                            {/* Motivo & Detalle */}
                            <td style={{ padding: '12px 14px' }}>
                              <div style={{ fontWeight: 700, color: '#0f2a4a' }}>
                                {req.reason}
                              </div>
                              {req.details && (
                                <div style={{ fontSize: '10px', color: 'rgba(15,42,74,0.6)', maxWidth: '240px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {req.details}
                                </div>
                              )}
                            </td>

                            {/* Periodo & Días */}
                            <td style={{ padding: '12px 14px', color: '#0f2a4a' }}>
                              <div style={{ fontWeight: 700 }}>
                                {new Date(req.startDate).toLocaleDateString('es-CO')} - {new Date(req.endDate).toLocaleDateString('es-CO')}
                              </div>
                              <div style={{ fontSize: '10px', color: '#0284c7', fontWeight: 800 }}>
                                {req.daysRequested} días hábiles
                              </div>
                            </td>

                            {/* Liquidación CST */}
                            <td style={{ padding: '12px 14px' }}>
                              <div style={{ fontWeight: 800, color: '#059669' }}>
                                ${(req.calculatedValue || 0).toLocaleString('es-CO')}
                              </div>
                              <div style={{ fontSize: '10px', color: 'rgba(15,42,74,0.5)' }}>
                                Base ${req.salaryBase ? `${req.salaryBase.toLocaleString('es-CO')}` : 'Sin registrar'}
                              </div>
                            </td>

                            {/* Estado */}
                            <td style={{ padding: '12px 14px' }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 10px',
                                borderRadius: '8px',
                                fontSize: '10px',
                                fontWeight: 800,
                                background: req.status === 'APROBADO' ? 'rgba(16,185,129,0.1)' : req.status === 'PENDIENTE' ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)',
                                color: req.status === 'APROBADO' ? '#059669' : req.status === 'PENDIENTE' ? '#d97706' : '#dc2626',
                                border: `1px solid ${req.status === 'APROBADO' ? 'rgba(16,185,129,0.2)' : req.status === 'PENDIENTE' ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)'}`
                              }}>
                                {req.status === 'APROBADO' && <IconCheck size={11} />}
                                {req.status === 'PENDIENTE' && <IconClock size={11} />}
                                {req.status === 'RECHAZADO' && <IconX size={11} />}
                                {req.status}
                              </span>
                            </td>

                            {/* Comprobante PDF */}
                            <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                              <button
                                onClick={() => handleDownloadVacationPDF(req.id, req.user.firstName)}
                                style={{
                                  background: 'rgba(2,132,199,0.08)',
                                  border: '1px solid rgba(2,132,199,0.2)',
                                  borderRadius: '8px',
                                  padding: '5px 10px',
                                  color: '#0284c7',
                                  fontSize: '11px',
                                  fontWeight: 800,
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <IconDownload size={12} /> PDF Oficial
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        )}

      </main>

      {/* MODAL ASIGNAR TAREA EN LIQUID GLASS */}
      {showNewTaskModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,42,74,0.45)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: '20px'
        }}>
          <div className="liquid-glass-modal" style={{ borderRadius: '22px', width: '100%', maxWidth: '500px', padding: '30px 34px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px', borderBottom: '1px solid rgba(15,42,74,0.08)', paddingBottom: '14px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#0f2a4a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IconPlus size={18} color="#0070f3" />
                <span>Asignar Nueva Tarea</span>
              </h3>
              <button
                onClick={() => setShowNewTaskModal(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: '1px solid rgba(15,42,74,0.1)',
                  background: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateTask} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>Título</label>
                <input
                  type="text"
                  required
                  placeholder="Ej: Carrusel Lanzamiento D49"
                  value={newTaskForm.title}
                  onChange={e => setNewTaskForm({ ...newTaskForm, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    borderRadius: '12px',
                    border: '1.5px solid rgba(15,42,74,0.12)',
                    background: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '13px',
                    boxSizing: 'border-box',
                    fontFamily: '"Montserrat", sans-serif',
                    outline: 'none',
                    fontWeight: 600
                  }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>Marca</label>
                  <select
                    required
                    value={newTaskForm.brandId}
                    onChange={e => setNewTaskForm({ ...newTaskForm, brandId: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '12px',
                      border: '1.5px solid rgba(15,42,74,0.12)',
                      background: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '13px',
                      boxSizing: 'border-box',
                      fontFamily: '"Montserrat", sans-serif',
                      outline: 'none',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Selecciona Marca</option>
                    {brands.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>Tipo</label>
                  <select
                    value={newTaskForm.type}
                    onChange={e => setNewTaskForm({ ...newTaskForm, type: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '12px',
                      border: '1.5px solid rgba(15,42,74,0.12)',
                      background: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '13px',
                      boxSizing: 'border-box',
                      fontFamily: '"Montserrat", sans-serif',
                      outline: 'none',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    <option value="ESTATICA">Estática</option>
                    <option value="CARRUSEL">Carrusel</option>
                    <option value="VIDEO">Video / Reel</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>Fecha de Entrega</label>
                  <input
                    type="date"
                    value={newTaskForm.publishDate}
                    onChange={e => setNewTaskForm({ ...newTaskForm, publishDate: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '12px',
                      border: '1.5px solid rgba(15,42,74,0.12)',
                      background: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '13px',
                      boxSizing: 'border-box',
                      fontFamily: '"Montserrat", sans-serif',
                      outline: 'none',
                      fontWeight: 600
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>Asignar a</label>
                  <select
                    value={newTaskForm.assignedToId}
                    onChange={e => setNewTaskForm({ ...newTaskForm, assignedToId: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '12px',
                      border: '1.5px solid rgba(15,42,74,0.12)',
                      background: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '13px',
                      boxSizing: 'border-box',
                      fontFamily: '"Montserrat", sans-serif',
                      outline: 'none',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">{inspectedUser?.firstName ? `${inspectedUser.firstName} ${inspectedUser.lastName}` : 'Seleccionar Creativo'}</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                type="submit"
                disabled={savingTask}
                style={{
                  marginTop: '8px',
                  padding: '13px',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #0070f3 0%, #0056d6 100%)',
                  color: '#ffffff',
                  fontWeight: 800,
                  fontSize: '13px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(0, 112, 243, 0.25)',
                  fontFamily: '"Montserrat", sans-serif',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 112, 243, 0.45)')}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 4px 14px rgba(0, 112, 243, 0.25)')}
              >
                {savingTask ? 'Guardando...' : 'Asignar Tarea'}
              </button>
            </form>
          </div>
        </div>
      )}


      {/* MODAL EDITAR COLABORADOR (ADMIN / CUENTAS) */}
      {editCollabModalOpen && selectedCollab && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,42,74,0.55)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: '20px'
        }}>
          <div className="liquid-glass-modal" style={{ borderRadius: '24px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto', padding: '32px 36px' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid rgba(15,42,74,0.08)', paddingBottom: '14px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#0f2a4a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <IconUsers size={20} color="#0070f3" />
                  <span>Editar Colaborador y RRHH</span>
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                  {selectedCollab.firstName} {selectedCollab.lastName} ({selectedCollab.email})
                </p>
              </div>
              <button
                onClick={() => setEditCollabModalOpen(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: '1px solid rgba(15,42,74,0.1)',
                  background: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveCollab} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              
              {/* Bloque 1: Identificación y Cargo */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>Nombres</label>
                  <input
                    type="text"
                    required
                    value={collabForm.firstName}
                    onChange={e => setCollabForm({ ...collabForm, firstName: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '1.5px solid rgba(15,42,74,0.12)',
                      background: 'rgba(255, 255, 255, 0.85)',
                      fontSize: '13px',
                      fontWeight: 600,
                      outline: 'none',
                      fontFamily: '"Montserrat", sans-serif',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>Apellidos</label>
                  <input
                    type="text"
                    required
                    value={collabForm.lastName}
                    onChange={e => setCollabForm({ ...collabForm, lastName: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '1.5px solid rgba(15,42,74,0.12)',
                      background: 'rgba(255, 255, 255, 0.85)',
                      fontSize: '13px',
                      fontWeight: 600,
                      outline: 'none',
                      fontFamily: '"Montserrat", sans-serif',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>Cargo / Posición en ZOIK</label>
                <input
                  type="text"
                  placeholder="Ej: Diseñador Gráfico & Multimedia, Editor de Video, Copywriter"
                  value={collabForm.position}
                  onChange={e => setCollabForm({ ...collabForm, position: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    border: '1.5px solid rgba(15,42,74,0.12)',
                    background: 'rgba(255, 255, 255, 0.85)',
                    fontSize: '13px',
                    fontWeight: 600,
                    outline: 'none',
                    fontFamily: '"Montserrat", sans-serif',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Bloque 2: Información Personal */}
              <div style={{ background: 'rgba(0,112,243,0.04)', borderRadius: '16px', padding: '16px', border: '1px solid rgba(0,112,243,0.12)', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <span style={{ fontSize: '11px', fontWeight: 900, color: '#0070f3', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Datos Personales y Ubicación
                </span>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: '14px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>Fecha de Nacimiento</label>
                    <input
                      type="date"
                      value={collabForm.birthDate}
                      onChange={e => setCollabForm({ ...collabForm, birthDate: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '12px',
                        border: '1.5px solid rgba(15,42,74,0.12)',
                        background: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '13px',
                        fontWeight: 600,
                        outline: 'none',
                        fontFamily: '"Montserrat", sans-serif',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>Dirección de Residencia</label>
                    <input
                      type="text"
                      placeholder="Ej: Cra 15 # 85 - 20, Apto 402, Bogotá"
                      value={collabForm.address}
                      onChange={e => setCollabForm({ ...collabForm, address: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '12px',
                        border: '1.5px solid rgba(15,42,74,0.12)',
                        background: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '13px',
                        fontWeight: 600,
                        outline: 'none',
                        fontFamily: '"Montserrat", sans-serif',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>Contacto de Emergencia (Nombre Completo)</label>
                    <input
                      type="text"
                      placeholder="Ej: María Gómez (Madre / Cónyuge)"
                      value={collabForm.emergencyContactName}
                      onChange={e => setCollabForm({ ...collabForm, emergencyContactName: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '12px',
                        border: '1.5px solid rgba(15,42,74,0.12)',
                        background: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '13px',
                        fontWeight: 600,
                        outline: 'none',
                        fontFamily: '"Montserrat", sans-serif',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>Teléfono de Emergencia</label>
                    <input
                      type="tel"
                      placeholder="Ej: +57 310 123 4567"
                      value={collabForm.emergencyContactPhone}
                      onChange={e => setCollabForm({ ...collabForm, emergencyContactPhone: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '12px',
                        border: '1.5px solid rgba(15,42,74,0.12)',
                        background: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '13px',
                        fontWeight: 600,
                        outline: 'none',
                        fontFamily: '"Montserrat", sans-serif',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>

              </div>

              {/* Bloque 3: Detalles de Contratación */}
              <div style={{ background: 'rgba(16,185,129,0.04)', borderRadius: '16px', padding: '16px', border: '1px solid rgba(16,185,129,0.15)', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <span style={{ fontSize: '11px', fontWeight: 900, color: '#047857', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Contrato Laboral y Fechas Límites
                </span>

                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>Tipo de Contrato</label>
                    <select
                      value={collabForm.contractType}
                      onChange={e => setCollabForm({ ...collabForm, contractType: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '12px',
                        border: '1.5px solid rgba(15,42,74,0.12)',
                        background: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '13px',
                        fontWeight: 600,
                        outline: 'none',
                        fontFamily: '"Montserrat", sans-serif',
                        boxSizing: 'border-box',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="Término Fijo">Término Fijo</option>
                      <option value="Término Indefinido">Término Indefinido</option>
                      <option value="Prestación de Servicios">Prestación de Servicios</option>
                      <option value="Obra o Labor">Obra o Labor</option>
                      <option value="Contrato de Aprendizaje">Contrato de Aprendizaje (SENA)</option>
                    </select>
                  </div>

                  {user?.role === 'ADMIN' && (
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>Salario Base Mensual (COP)</label>
                      <input
                        type="number"
                        placeholder="Ej: 2500000"
                        value={collabForm.baseSalary}
                        onChange={e => setCollabForm({ ...collabForm, baseSalary: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          borderRadius: '12px',
                          border: '1.5px solid rgba(15,42,74,0.12)',
                          background: 'rgba(255, 255, 255, 0.9)',
                          fontSize: '13px',
                          fontWeight: 600,
                          outline: 'none',
                          fontFamily: '"Montserrat", sans-serif',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>Inicio de Contrato</label>
                    <input
                      type="date"
                      value={collabForm.contractStart}
                      onChange={e => setCollabForm({ ...collabForm, contractStart: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '12px',
                        border: '1.5px solid rgba(15,42,74,0.12)',
                        background: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '13px',
                        fontWeight: 600,
                        outline: 'none',
                        fontFamily: '"Montserrat", sans-serif',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>
                      Finalización de Contrato
                    </label>
                    <input
                      type="date"
                      value={collabForm.contractEnd}
                      onChange={e => setCollabForm({ ...collabForm, contractEnd: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '12px',
                        border: '1.5px solid rgba(15,42,74,0.12)',
                        background: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '13px',
                        fontWeight: 600,
                        outline: 'none',
                        fontFamily: '"Montserrat", sans-serif',
                        boxSizing: 'border-box'
                      }}
                    />
                    <span style={{ fontSize: '10px', color: '#64748b', display: 'block', marginTop: '4px' }}>
                      ℹ️ Se enviará alerta automática 1 mes antes a Alejandra Fernández.
                    </span>
                  </div>
                </div>

              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={() => setEditCollabModalOpen(false)}
                  style={{
                    padding: '11px 20px',
                    borderRadius: '12px',
                    border: '1.5px solid rgba(15,42,74,0.15)',
                    background: 'rgba(255,255,255,0.8)',
                    color: '#0f2a4a',
                    fontWeight: 800,
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingCollab}
                  style={{
                    padding: '11px 26px',
                    borderRadius: '12px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #0070f3 0%, #0056d6 100%)',
                    color: '#ffffff',
                    fontWeight: 800,
                    fontSize: '13px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 14px rgba(0, 112, 243, 0.25)',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                >
                  {savingCollab ? 'Guardando...' : 'Guardar Colaborador'}
                </button>
              </div>

            </form>

          </div>
        </div>
      )}


      {/* MODAL REGISTRAR HORAS EXTRAS */}
      {showRegisterOvertimeModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,42,74,0.55)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          padding: '20px'
        }}>
          <div className="liquid-glass-modal" style={{ borderRadius: '24px', width: '100%', maxWidth: '520px', padding: '30px 34px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1px solid rgba(15,42,74,0.08)', paddingBottom: '12px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#0f2a4a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <IconClock size={18} color="#0070f3" />
                  <span>Registrar Horas Extras</span>
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                  Se enviará automáticamente a Nicolás Fernández para aprobación formal.
                </p>
              </div>
              <button
                onClick={() => setShowRegisterOvertimeModal(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: '1px solid rgba(15,42,74,0.1)',
                  background: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleRegisterOvertimeSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>
                  Fecha de la Jornada
                </label>
                <input
                  type="date"
                  required
                  value={registerOvertimeForm.date}
                  onChange={e => setRegisterOvertimeForm({ ...registerOvertimeForm, date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    border: '1.5px solid rgba(15,42,74,0.12)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '13px',
                    fontWeight: 600,
                    outline: 'none',
                    fontFamily: '"Montserrat", sans-serif',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>
                    Hora de Inicio
                  </label>
                  <input
                    type="time"
                    required
                    value={registerOvertimeForm.startTime}
                    onChange={e => setRegisterOvertimeForm({ ...registerOvertimeForm, startTime: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '1.5px solid rgba(15,42,74,0.12)',
                      background: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '13px',
                      fontWeight: 600,
                      outline: 'none',
                      fontFamily: '"Montserrat", sans-serif',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>
                    Hora de Fin
                  </label>
                  <input
                    type="time"
                    required
                    value={registerOvertimeForm.endTime}
                    onChange={e => setRegisterOvertimeForm({ ...registerOvertimeForm, endTime: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '1.5px solid rgba(15,42,74,0.12)',
                      background: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '13px',
                      fontWeight: 600,
                      outline: 'none',
                      fontFamily: '"Montserrat", sans-serif',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>
                  Motivo / Proyecto / Marca Justificada
                </label>
                <textarea
                  required
                  rows={3}
                  placeholder="Ej: Entrega urgente de piezas para campaña Black Friday Edwcar, soporte en vivo."
                  value={registerOvertimeForm.reason}
                  onChange={e => setRegisterOvertimeForm({ ...registerOvertimeForm, reason: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    border: '1.5px solid rgba(15,42,74,0.12)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '12px',
                    fontWeight: 600,
                    outline: 'none',
                    fontFamily: '"Montserrat", sans-serif',
                    boxSizing: 'border-box',
                    resize: 'none'
                  }}
                />
              </div>

              <div style={{ background: 'rgba(0,112,243,0.06)', borderRadius: '12px', padding: '12px 14px', border: '1px solid rgba(0,112,243,0.15)', fontSize: '11px', color: '#0369a1', lineHeight: 1.4 }}>
                ℹ️ <strong>Cálculo Automatizado:</strong> Tras la aprobación de Nicolás Fernández, el sistema calculará automáticamente las franjas diurna (25%), nocturna (75%) y festivas (100%/150%), generando el comprobante PDF oficial para el perfil contable de Alejandra Fernández.
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '6px' }}>
                <button
                  type="button"
                  onClick={() => setShowRegisterOvertimeModal(false)}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '12px',
                    border: '1.5px solid rgba(15,42,74,0.15)',
                    background: 'rgba(255,255,255,0.85)',
                    color: '#0f2a4a',
                    fontWeight: 800,
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingOvertime}
                  style={{
                    padding: '10px 22px',
                    borderRadius: '12px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #0070f3 0%, #0056d6 100%)',
                    color: '#ffffff',
                    fontWeight: 800,
                    fontSize: '12px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 14px rgba(0, 112, 243, 0.25)',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                >
                  {savingOvertime ? 'Enviando...' : 'Enviar para Aprobación'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* MODAL RADICAR SOLICITUD DE VACACIONES (MÓDULO 5) */}
      {showRequestVacationModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,42,74,0.55)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255,255,255,0.8)',
            boxShadow: '0 25px 60px rgba(15,42,74,0.25)',
            width: '100%',
            maxWidth: '540px',
            padding: '28px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            fontFamily: '"Montserrat", sans-serif'
          }}>
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '14px',
                  background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '22px'
                }}>
                  🌴
                </div>
                <div>
                  <h3 style={{ fontSize: '17px', fontWeight: 900, color: '#0f2a4a', margin: 0 }}>
                    Radicación de Vacaciones & Descanso
                  </h3>
                  <p style={{ fontSize: '12px', color: 'rgba(15,42,74,0.6)', margin: '3px 0 0 0' }}>
                    Formulario formal de RRHH — Generación de comprobante en PDF con radicado ZOIK
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowRequestVacationModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'rgba(15,42,74,0.4)',
                  padding: '4px'
                }}
              >
                <IconX size={20} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleRequestVacationSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Destinatario */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.7)', marginBottom: '6px', textTransform: 'uppercase' }}>
                  ¿A quién va dirigida la solicitud? *
                </label>
                <select
                  required
                  value={requestVacationForm.recipientName}
                  onChange={(e) => setRequestVacationForm({ ...requestVacationForm, recipientName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '12px',
                    border: '1px solid rgba(15,42,74,0.15)',
                    background: '#ffffff',
                    fontSize: '13px',
                    color: '#0f2a4a',
                    fontWeight: 600,
                    outline: 'none',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                >
                  <option value="Alejandra Fernández — Directora de Contabilidad & RRHH">
                    Alejandra Fernández — Directora de Contabilidad & RRHH
                  </option>
                  <option value="Nicolás Fernández — Operaciones & Producción">
                    Nicolás Fernández — Operaciones & Producción
                  </option>
                  <option value="Vicente Sosa — Dirección General">
                    Vicente Sosa — Dirección General
                  </option>
                </select>
              </div>

              {/* Motivo de la solicitud */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.7)', marginBottom: '6px', textTransform: 'uppercase' }}>
                  Motivo de la solicitud *
                </label>
                <select
                  required
                  value={requestVacationForm.reason}
                  onChange={(e) => setRequestVacationForm({ ...requestVacationForm, reason: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '12px',
                    border: '1px solid rgba(15,42,74,0.15)',
                    background: '#ffffff',
                    fontSize: '13px',
                    color: '#0f2a4a',
                    fontWeight: 600,
                    outline: 'none',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                >
                  <option value="Vacaciones Legales Anuales (CST Art. 186)">
                    Vacaciones Legales Anuales (CST Art. 186 - 15 días hábiles)
                  </option>
                  <option value="Vacaciones Anticipadas">
                    Vacaciones Anticipadas (Fracción de periodo)
                  </option>
                  <option value="Compensación en Dinero (CST Art. 189)">
                    Compensación de Vacaciones en Dinero (CST Art. 189)
                  </option>
                  <option value="Permiso Remunerado Especial">
                    Permiso Remunerado Especial / Calamidad Doméstica
                  </option>
                </select>
              </div>

              {/* Rango de Fechas & Días Hábiles */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.7)', marginBottom: '6px', textTransform: 'uppercase' }}>
                    Fecha Inicio *
                  </label>
                  <input
                    type="date"
                    required
                    value={requestVacationForm.startDate}
                    onChange={(e) => setRequestVacationForm({ ...requestVacationForm, startDate: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 10px',
                      borderRadius: '12px',
                      border: '1px solid rgba(15,42,74,0.15)',
                      background: '#ffffff',
                      fontSize: '12px',
                      color: '#0f2a4a',
                      outline: 'none',
                      fontFamily: '"Montserrat", sans-serif'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.7)', marginBottom: '6px', textTransform: 'uppercase' }}>
                    Fecha Fin *
                  </label>
                  <input
                    type="date"
                    required
                    value={requestVacationForm.endDate}
                    onChange={(e) => setRequestVacationForm({ ...requestVacationForm, endDate: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 10px',
                      borderRadius: '12px',
                      border: '1px solid rgba(15,42,74,0.15)',
                      background: '#ffffff',
                      fontSize: '12px',
                      color: '#0f2a4a',
                      outline: 'none',
                      fontFamily: '"Montserrat", sans-serif'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.7)', marginBottom: '6px', textTransform: 'uppercase' }}>
                    Días Hábiles *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    max="30"
                    value={requestVacationForm.daysRequested}
                    onChange={(e) => setRequestVacationForm({ ...requestVacationForm, daysRequested: parseInt(e.target.value) || 1 })}
                    style={{
                      width: '100%',
                      padding: '10px 10px',
                      borderRadius: '12px',
                      border: '1px solid rgba(15,42,74,0.15)',
                      background: '#ffffff',
                      fontSize: '12px',
                      color: '#0f2a4a',
                      fontWeight: 800,
                      outline: 'none',
                      fontFamily: '"Montserrat", sans-serif'
                    }}
                  />
                </div>
              </div>

              {/* Detalle y Solicitud */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.7)', marginBottom: '6px', textTransform: 'uppercase' }}>
                  Detalle y Exposición de la Solicitud
                </label>
                <textarea
                  rows={3}
                  value={requestVacationForm.details}
                  onChange={(e) => setRequestVacationForm({ ...requestVacationForm, details: e.target.value })}
                  placeholder="Describe las particularidades de tu solicitud, fechas acordadas con tu equipo o proyectos que quedan cubiertos..."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '12px',
                    border: '1px solid rgba(15,42,74,0.15)',
                    background: '#ffffff',
                    fontSize: '12px',
                    color: '#0f2a4a',
                    outline: 'none',
                    resize: 'none',
                    fontFamily: '"Montserrat", sans-serif'
                  }}
                />
              </div>

              {/* Pre-liquidación Legal en Vivo */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(2,132,199,0.08) 0%, rgba(3,105,161,0.04) 100%)',
                border: '1px solid rgba(2,132,199,0.2)',
                borderRadius: '14px',
                padding: '14px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: '#0284c7', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Pre-Liquidación Reglamentaria (CST Art. 186-192)
                  </span>
                  <span style={{ fontSize: '11px', fontWeight: 900, color: '#0f2a4a' }}>
                    {requestVacationForm.daysRequested} días hábiles
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px', color: 'rgba(15,42,74,0.7)' }}>
                  <div>
                    Salario Base:{' '}
                    <strong style={{ color: '#0f2a4a' }}>
                      ${((user as any)?.baseSalary || 2000000).toLocaleString('es-CO')}
                    </strong>
                  </div>
                  <div>
                    Base Diaria (Salario / 30):{' '}
                    <strong style={{ color: '#0f2a4a' }}>
                      ${Math.round(((user as any)?.baseSalary || 2000000) / 30).toLocaleString('es-CO')}
                    </strong>
                  </div>
                </div>

                <div style={{ borderTop: '1px solid rgba(2,132,199,0.15)', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a' }}>
                    Total Liquidación Estimada:
                  </span>
                  <span style={{ fontSize: '16px', fontWeight: 900, color: '#059669' }}>
                    ${Math.round((((user as any)?.baseSalary || 2000000) / 30) * (requestVacationForm.daysRequested || 1)).toLocaleString('es-CO')}
                  </span>
                </div>
                <div style={{ fontSize: '10px', color: 'rgba(15,42,74,0.5)', fontStyle: 'italic' }}>
                  Al radicar, se generará el documento formal en PDF membretado con logo ZOIK y se notificará a Alejandra Fernández (contabilidadzoikagency@gmail.com).
                </div>
              </div>

              {/* Botones Acciones */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px' }}>
                <button
                  type="button"
                  onClick={() => setShowRequestVacationModal(false)}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(15,42,74,0.15)',
                    background: '#ffffff',
                    color: '#0f2a4a',
                    fontSize: '12px',
                    fontWeight: 800,
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingVacation}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '12px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                    color: '#ffffff',
                    fontSize: '12px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    boxShadow: '0 6px 18px rgba(2,132,199,0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <span>{savingVacation ? 'Radicando...' : '🌴 Radicar Solicitud Oficial'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Team;
